// IE Hub - Onboarding Page
// Multi-step onboarding flow with form collection and illustration
//
// VULNERABILITY FLOW LOGIC:
// - Steps 1-8: Standard onboarding flow (post-code moved to separate page)
// - Step 8 (index 7): Vulnerability circumstances question (yes/no)
// - If "No": Skip to completion screen
// - If "Yes": Continue to 3 extra steps (details, circumstances selection, consent)
// - After last step: Completion screen

import React, {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  IconButton,
  LogoutIcon,
  Menu,
  MenuIcon,
  MenuItem,
  Typography,
} from "../../../ui";
// Global Help is provided by HelpProvider
import { useHelp } from "@/shared/help/HelpProvider";
import { useSearchParams } from "react-router-dom";
import Screen from "../../../engine/renderer/Screen";
import { useAuth, useForm, useUI } from "../../../shared/state/store";
import { useNavigate } from "react-router-dom";
import { OnboardingService } from "../../../shared/api/services/onboardingService";
import { LenderService } from "../../../shared/api/services/lenderService";
import type {
  LenderParentRecord,
  AccountTypeRecord,
} from "../../../shared/api/services/lenderService";
import { TermsService } from "../../../shared/api/services/termsService";
import { ProfileService } from "../../../shared/api/services/profileService";
import { DebtsService } from "../../../shared/api/services/debtsService";
import type { CustomerSharingUpsertRequest } from "@/shared/api/types";
import type { CustomerProfileData } from "@/shared/api/types";
import {
  getAddressFromGooglePlacesWithTimeout,
  buildEnrichedAddressData,
  type EnrichedAddressData,
} from "@/shared/lib/googlePlaces";

// Import Schema type from Screen component to avoid type conflicts
type ScreenSchema = Parameters<typeof Screen>[0]["schema"];

// Import onboarding schemas
import referedMethod from "../../../engine/schema/onboarding/2-refered-method.json";
import referedFromCompany from "../../../engine/schema/onboarding/3-refered-from-company.json";
import reasonIEHub from "../../../engine/schema/onboarding/4-reason-iehub.json";
import personalDetails from "../../../engine/schema/onboarding/5-personal-details.json";
import dateOfBirth from "../../../engine/schema/onboarding/6-date-of-birth.json";
import contactDetails from "../../../engine/schema/onboarding/7-contact-details.json";
import addressDetails from "../../../engine/schema/onboarding/8-address-details.json";
import vulnerabilityCircumstances from "../../../engine/schema/onboarding/9-vulnerability-circumstances.json";
import yesVulnerabilityWho from "../../../engine/schema/onboarding/10-yes-vulnerability.json";
import yesVulnerabilityCircumstancesSelection from "../../../engine/schema/onboarding/11-yes-vulnerability.json";
import yesVulnerabilityConsent from "../../../engine/schema/onboarding/12-yes-vulnerability.json";

import Footer from "@/components/on-boarding/Footer";
import OnboardingCompletion from "../../../components/OnboardingCompletion";
import SimpleLoadingScreen from "../../../components/SimpleLoadingScreen";
import { processAddressVerification } from "@/shared/lib/helper";
import { parseUnknownError } from "@/shared/lib/errorHandling";
import ErrorDialog from "@/ui/custom/ErrorDialog";
import { useDOMErrorSuppression } from "@/shared/hooks/useDOMErrorSuppression";
import {
  BARCLAYS_MM_LENDER_NAME,
  isPrearrearsCustomer,
} from "@/shared/prearrears/ctToken";
import { trackMetaEvent } from "@/shared/tracking/metaTracking";
// import CustomDialog from '@/ui/custom/CustomDialog';

// Onboarding schemas array (post-code handled separately in AddressVerification)
const onboardingSchemas: ScreenSchema[] = [
  referedMethod as unknown as ScreenSchema,
  referedFromCompany as unknown as ScreenSchema,
  reasonIEHub as unknown as ScreenSchema,
  personalDetails as unknown as ScreenSchema,
  dateOfBirth as unknown as ScreenSchema,
  contactDetails as unknown as ScreenSchema,
  addressDetails as unknown as ScreenSchema,
  vulnerabilityCircumstances as unknown as ScreenSchema,
];

// Conditional steps appended after step 9 when user selects "Yes"
const vulnerabilityYesSchemas: ScreenSchema[] = [
  yesVulnerabilityWho as unknown as ScreenSchema,
  yesVulnerabilityCircumstancesSelection as unknown as ScreenSchema,
  yesVulnerabilityConsent as unknown as ScreenSchema,
];

// Carousel images
const carouselImages = [
  "/assets/onBoarding/crausal1.webp",
  "/assets/onBoarding/crausal2.webp",
  "/assets/onBoarding/crausal3.webp",
  "/assets/onBoarding/crausal4.webp",
];

interface CompanySelectOption {
  value: string;
  label: string;
  childCompany: string[];
  referenceNumberFormat?: string;
  showCompanyList: boolean;
}

const OTHER_COMPANY_OPTION: CompanySelectOption = {
  value: "OTHER_COMPANY",
  label: "Can't find my referring company",
  childCompany: [],
  showCompanyList: false,
};

const getString = (
  record: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  if (record == null) {
    return undefined;
  }
  const value = record[key];
  return typeof value === "string" ? value : undefined;
};

const getBoolean = (
  record: Record<string, unknown> | undefined,
  key: string
): boolean | undefined => {
  if (record == null) {
    return undefined;
  }
  const value = record[key];
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (
      normalized === "true" ||
      normalized === "y" ||
      normalized === "yes" ||
      normalized === "1"
    ) {
      return true;
    }
    if (
      normalized === "false" ||
      normalized === "n" ||
      normalized === "no" ||
      normalized === "0"
    ) {
      return false;
    }
  }
  return undefined;
};

const normalizeString = (value: unknown): string => {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const isAffirmative = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === "y" ||
      normalized === "yes" ||
      normalized === "true" ||
      normalized === "1"
    );
  }
  return false;
};

const toYN = (value: unknown, fallback: "Y" | "N" = "N"): "Y" | "N" => {
  if (value === "Y") return "Y";
  if (value === "N") return "N";
  return fallback;
};

// When a referred/reference company is added to the Share List from referral flows,
// we default it to selected (shareNow = "Y").
const shouldDefaultShareNow = (_lenderName: string): boolean => true;
// YWCT , VRS , Payplan , National debtline
// NOTE: Barclays MM is conditionally hidden unless the user is pre-arrears.
const HIDDEN_COMPANY_NAMES = [
  "Yorkshire Water Community Trust",
  "Vulnerability Registration Service",
  "PayPlan",
  "National Debtline",
];
const hiddenCompanyNameSet = new Set(
  HIDDEN_COMPANY_NAMES.map((name) => normalizeString(name))
);
const BARCLAYS_MM_KEY = normalizeString(BARCLAYS_MM_LENDER_NAME);

const findLenderByName = (
  lookup: Map<string, LenderParentRecord>,
  lendersList: LenderParentRecord[],
  name: string | undefined
): LenderParentRecord | undefined => {
  if (!name) {
    return undefined;
  }
  const normalized = normalizeString(name);
  const trimmedOriginal =
    typeof name === "string" ? name.trim().toLowerCase() : "";
  const fromLookup = lookup.get(normalized);
  if (fromLookup) {
    return fromLookup;
  }

  // Attempt to match against composite keys (parent + child)
  for (const record of lendersList) {
    if (!record) continue;
    const parentKeyParts = new Set<string>();
    const parentCompanyKey = normalizeString(record.parentCompany);
    if (parentCompanyKey) parentKeyParts.add(parentCompanyKey);
    const descriptionKey = normalizeString(record.description);
    if (descriptionKey) parentKeyParts.add(descriptionKey);
    const parentRaw = record.raw as
      | { parentCompany?: string; lender?: string }
      | undefined;
    const parentRootKey = normalizeString(parentRaw?.parentCompany);
    if (parentRootKey) {
      parentKeyParts.add(parentRootKey);
    }
    const lenderKey = normalizeString((record as { lender?: string }).lender);
    if (lenderKey) {
      parentKeyParts.add(lenderKey);
    }

    const childCompanies = Array.isArray(record.childCompany)
      ? record.childCompany
      : [];
    for (const childName of childCompanies) {
      const childNormalized = normalizeString(childName);
      if (!childNormalized) {
        continue;
      }
      if (
        childNormalized === normalized ||
        childNormalized === trimmedOriginal
      ) {
        return record;
      }
      // Composite key "parent|child" and "child|parent"
      for (const parentKey of parentKeyParts) {
        if (!parentKey) {
          continue;
        }
        const compositeA = `${parentKey}|${childNormalized}`;
        const compositeB = `${childNormalized}|${parentKey}`;
        if (
          normalized === compositeA ||
          normalized === compositeB ||
          trimmedOriginal === compositeA ||
          trimmedOriginal === compositeB
        ) {
          return record;
        }
      }
    }

    const candidates: Array<unknown> = [
      record.parentCompany,
      record.description,
      (record as { lender?: string }).lender,
      (record.raw as { parentCompany?: string; lender?: string } | undefined)
        ?.parentCompany,
      (record.raw as { parentCompany?: string; lender?: string } | undefined)
        ?.lender,
    ];
    if (
      candidates.some((candidate) => {
        const normalizedCandidate = normalizeString(candidate);
        return (
          normalizedCandidate === normalized ||
          normalizedCandidate === trimmedOriginal
        );
      })
    ) {
      return record;
    }
  }

  return undefined;
};

// Helper function to check if fields should be disabled based on agent pre-filled data
const shouldDisableFields = (
  customerData: Record<string, unknown> | null,
  debtsData: Record<string, unknown>[] | null,
  options: {
    hasProfileDebt: boolean;
  }
): boolean => {
  if (!customerData || !debtsData || debtsData.length === 0) {
    return false;
  }

  const textField2 = customerData.textField2;
  const textField3 = customerData.textField3;
  const field5TxtFld = customerData.field5TxtFld;
  const field5TxtFldHasData = Boolean(
    field5TxtFld && String(field5TxtFld).trim().length > 0
  );
  const customerDebt = debtsData[0];
  const customerDebtCreditor = customerDebt?.creditor;
  const customerDebtAccountNumber = customerDebt?.accountNumber;

  return (
    textField2 === "Creditor" &&
    Boolean(customerDebtCreditor) &&
    textField3 === field5TxtFld &&
    field5TxtFldHasData &&
    options.hasProfileDebt &&
    Boolean(customerDebtAccountNumber)
  );
};

interface OnboardingErrorBoundaryProps {
  children: React.ReactNode;
  onGoBack: () => void;
  stepLabel?: string;
}

interface OnboardingErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class OnboardingErrorBoundary extends Component<
  OnboardingErrorBoundaryProps,
  OnboardingErrorBoundaryState
> {
  constructor(props: OnboardingErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): OnboardingErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error) {
    console.error("Onboarding screen error:", error);
    try {
      const fema = (window as unknown as { fema?: { captureError?: (e: Error, ctx: Record<string, unknown>) => void } }).fema;
      if (fema && typeof fema.captureError === "function") {
        fema.captureError(error, {
          context: "OnboardingErrorBoundary",
          step: this.props.stepLabel,
          url: window.location.href,
          timestamp: new Date().toISOString(),
        });
      }
    } catch {
      // non-blocking
    }
  }

  override componentDidUpdate(prevProps: OnboardingErrorBoundaryProps) {
    if (prevProps.stepLabel !== this.props.stepLabel && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            py: 6,
            px: 3,
            gap: 2,
          }}
        >
          <Typography variant="h6" color="error.main">
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
            We encountered an issue loading this step. Please go back and try
            again. If the problem persists, contact support.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={this.props.onGoBack}
            sx={{ mt: 1 }}
          >
            Go Back
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function NewOnBoardingScreen(): JSX.Element {
  useDOMErrorSuppression(true);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const mobileContentRef = useRef<HTMLDivElement | null>(null);
  const desktopContentRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(100);
  const [footerHeight, setFooterHeight] = useState<number>(72);

  const [searchParams, setSearchParams] = useSearchParams();
  // Step management
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const raw = searchParams.get("step");
    const parsed = raw != null ? parseInt(raw, 10) : 0;
    return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Help handled by HelpProvider
  const [logoutMenuAnchor, setLogoutMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogTitle, setErrorDialogTitle] = useState<string>("");
  const [errorDialogMessage, setErrorDialogMessage] = useState<string>("");
  // No loading screen here; address verification is now a separate page

  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { addAlert, setLoading, loading } = useUI();

  // Form state management
  const { setFormData, formData: allFormData } = useForm();
  const [formInstanceKeys, setFormInstanceKeys] = useState<
    Record<number, number>
  >({});

  // Prefill state management
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  const [lenders, setLenders] = useState<LenderParentRecord[]>([]);
  const [lendersLoaded, setLendersLoaded] = useState(false);
  const [emailPrefillAttempted, setEmailPrefillAttempted] = useState(false);

  const [schemaOverrides, setSchemaOverrides] = useState<
    Record<number, ScreenSchema>
  >({});
  const [accountTypeOptions, setAccountTypeOptions] = useState<
    AccountTypeRecord[]
  >([]);
  const [accountTypeCompanyKey, setAccountTypeCompanyKey] = useState<
    string | null
  >(null);
  const autoSelectedAccountTypeRef = useRef<string | null>(null);
  const lastSchemaOverrideJsonRef = useRef<string>("");

  // Customer and debts data for field disabling conditions
  const [customerData, setCustomerData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [debtsData, setDebtsData] = useState<Record<string, unknown>[] | null>(
    null
  );
  const isPrearrears = useMemo(
    () => isPrearrearsCustomer(customerData),
    [customerData]
  );
  const hasProfileDebt = useMemo(() => {
    if (!customerData || !Array.isArray(debtsData)) {
      return false;
    }
    const firstDebt = debtsData[0];
    const profileDebtFlag = getBoolean(
      firstDebt as Record<string, unknown>,
      "hasProfileDebt"
    );
    if (typeof profileDebtFlag === "boolean") {
      return profileDebtFlag;
    }
    const customerFlag = getBoolean(customerData, "hasProfileDebt");
    return Boolean(customerFlag);
  }, [customerData, debtsData]);

  // If agent/customer record indicates Creditor flow, skip referral steps (0 and 1)
  const skipReferralSteps = useMemo(() => {
    // Prefer localStorage snapshot (available immediately) then fallback to state
    try {
      const raw = localStorage.getItem("customerData");
      if (raw) {
        const obj = JSON.parse(raw) as Record<string, unknown> | null;
        if (obj && (obj as Record<string, any>)?.textField2 === "Creditor") {
          return true;
        }
      }
    } catch {
      // ignore
    }
    try {
      return (
        (customerData as Record<string, any> | null)?.textField2 === "Creditor"
      );
    } catch {
      return false;
    }
  }, [customerData]);

  const earliestStep = skipReferralSteps ? 2 : 0;

  // Field stage persistence refs
  const fieldStageInitializedRef = useRef<boolean>(false);
  const fieldStageRecordIdRef = useRef<number | null>(null);
  const fieldStageCustomerIdRef = useRef<number | null>(null);
  const lastPersistedStageRef = useRef<string | null>(null);
  const step1ClearedRef = useRef<boolean>(false);

  // Screen component props
  const hideHeader = true;
  const hideActions = true;

  // URL step parsing and clamping helpers
  const parseStepFromParams = (params: URLSearchParams): number => {
    const raw = params.get("step");
    const parsed = raw != null ? parseInt(raw, 10) : 0;
    return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
  };

  const clampStep = (stepIndex: number, total: number): number => {
    if (total <= 0) return 0;
    if (stepIndex < 0) return 0;
    const maxIndex = total - 1;
    return stepIndex > maxIndex ? maxIndex : stepIndex;
  };

  // Determine if vulnerability follow-up steps should be included based on vulnerability step answer
  const computeShouldIncludeFromData = (
    data: Record<string, unknown>
  ): boolean => {
    // Vulnerability step is last base step index (after removal starts at 0..7 where 7 is vulnerability)
    const vulnerabilityStepKey = `onboarding-step-7`;
    const stepData =
      (data?.[vulnerabilityStepKey] as Record<string, unknown> | undefined) ||
      {};
    const value = stepData?.hasVulnerabilityCircumstances as unknown;
    const shouldInclude = value === "yes";

    return shouldInclude;
  };

  // Active schemas list (base + conditional)
  const activeSchemas: ScreenSchema[] = useMemo(() => {
    const includeExtras = computeShouldIncludeFromData(
      allFormData as unknown as Record<string, unknown>
    );
    const baseSchemas = includeExtras
      ? [...onboardingSchemas, ...vulnerabilityYesSchemas]
      : onboardingSchemas;
    return baseSchemas.map((schema, index) => schemaOverrides[index] ?? schema);
  }, [allFormData, schemaOverrides]);

  // Current schema and total steps derived from active list
  const currentSchema: ScreenSchema = useMemo(() => {
    const schema = (activeSchemas[currentStep] ??
      onboardingSchemas[0]) as ScreenSchema;
    return schema;
  }, [activeSchemas, currentStep]);
  const totalSteps = activeSchemas.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const screenRenderKey = `${currentStep}-${formInstanceKeys[currentStep] ?? 0}`;

  // Live form values for the current step (from Screen)
  const liveStepValuesRef = useRef<Record<string, unknown>>({});
  // Live selection for vulnerability question to trigger re-render on change
  const [vulnerabilitySelection, setVulnerabilitySelection] = useState<
    string | undefined
  >(undefined);
  // Live selection for company on step 1 to drive dynamic schema without submit
  const [companySelectionLive, setCompanySelectionLive] = useState<
    string | undefined
  >(undefined);
  const [notReferredByCompanyLive, setNotReferredByCompanyLive] = useState<
    boolean | undefined
  >(undefined);
  const [productSelectionLive, setProductSelectionLive] = useState<
    string | undefined
  >(undefined);
  const lastCompanySelectionRef = useRef<string | undefined>(undefined);
  const lastNoReferenceIdRef = useRef<boolean | undefined>(undefined);
  // Guard to avoid repeated fetch restore attempts when returning to step 1
  const productRestoreAttemptedRef = useRef<boolean>(false);

  // Note: Step 6 address enrichment is now done synchronously in handleFormSubmit
  // Google API is called before the PUT request, no debouncing needed

  // Schema-driven button texts and special behaviors
  const formHasNoFields = useMemo(() => {
    try {
      return (
        (currentSchema.sections || []).reduce(
          (sum, sec) => sum + (sec.fields || []).length,
          0
        ) === 0
      );
    } catch {
      return false;
    }
  }, [currentSchema]);

  const actionsConfig = currentSchema.actions;
  const buttonsConfig = actionsConfig?.buttons;

  const computedPrimaryText = useMemo(() => {
    // On vulnerability step with no selection, show "Skip & continue" on the primary button
    if (currentStep === 7) {
      const selected =
        vulnerabilitySelection ??
        ((
          allFormData["onboarding-step-7"] as
            | Record<string, unknown>
            | undefined
        )?.hasVulnerabilityCircumstances as string | undefined) ??
        (liveStepValuesRef.current?.hasVulnerabilityCircumstances as
          | string
          | undefined);
      if (!selected) return "Skip & continue";
    }
    const defaultText =
      currentStep === totalSteps - 1 ? "Complete" : "Continue";
    // Prefer schema buttons.primaryText over legacy nextButtonText
    return (
      buttonsConfig?.primaryText || actionsConfig?.nextButtonText || defaultText
    );
  }, [
    actionsConfig,
    buttonsConfig,
    currentStep,
    totalSteps,
    vulnerabilitySelection,
    allFormData,
  ]);

  const shouldShowSecondary = useMemo(() => {
    return Boolean(
      (buttonsConfig?.showSecondaryWhenNoFields && formHasNoFields) ||
        buttonsConfig?.showSecondary
    );
  }, [buttonsConfig, formHasNoFields]);

  const secondaryButtonText = useMemo(() => {
    return buttonsConfig?.secondaryText || "Skip & continue";
  }, [buttonsConfig]);

  // Optional: central place to customize step routing
  const getNextStepIndex = (
    stepIndex: number,
    mergedData: Record<string, unknown>
  ): number => {
    // Linear progression across the dynamically computed active schema list
    const includeExtras = computeShouldIncludeFromData(mergedData);
    const nextLength = includeExtras
      ? onboardingSchemas.length + vulnerabilityYesSchemas.length
      : onboardingSchemas.length;

    // Simply move to next step, but don't exceed the total length
    const nextStep = stepIndex + 1;
    return Math.min(nextStep, nextLength - 1);
  };

  // Navigate to a specific step and reflect it in the URL (?step=X)
  const goToStep = (
    nextIndex: number,
    options?: { replace?: boolean; assumeTotalSteps?: number }
  ): void => {
    const clampTotal = options?.assumeTotalSteps ?? totalSteps;
    const clamped = clampStep(nextIndex, clampTotal);
    const finalIndex = Math.max(clamped, earliestStep);

    // Keep the max-reached marker in sync so the forward-jump guard
    // doesn't block legitimate navigation.  Only advance, never regress.
    const currentMax = parseInt(
      lastPersistedStageRef.current ?? "-1",
      10
    );
    if (finalIndex > currentMax) {
      lastPersistedStageRef.current = String(finalIndex);
    }

    if (finalIndex !== currentStep) {
      setCurrentStep(finalIndex);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("step", String(finalIndex));
    setSearchParams(nextParams, { replace: options?.replace ?? false });
  };

  // Persist field stage helper — returns a promise so critical call sites can await it
  const persistFieldStage = (stageIndex: number): Promise<void> => {
    const customerId = fieldStageCustomerIdRef.current;
    if (!customerId || stageIndex < 0) return Promise.resolve();
    const stageStr = String(stageIndex);
    if (lastPersistedStageRef.current === stageStr) return Promise.resolve();
    return OnboardingService.upsertCustomerFieldStage(
      {
        customerId,
        fieldStage: stageStr,
        userId: user?.userId as number,
      },
      { skipErrorCapture: true }
    )
      .then((saved) => {
        if (saved) {
          fieldStageRecordIdRef.current =
            typeof saved.id === "number"
              ? saved.id
              : fieldStageRecordIdRef.current;
          lastPersistedStageRef.current = stageStr;
        }
      })
      .catch(() => {
        /* no-op */
      });
  };

  // Ensure we have a customer id available before persisting stage
  const ensureFieldStageCustomerId = async (): Promise<number | null> => {
    const existing = fieldStageCustomerIdRef.current;
    if (typeof existing === "number" && existing > 0) {
      return existing;
    }
    try {
      const customerDataStr = localStorage.getItem("customerData");
      if (customerDataStr) {
        const obj = JSON.parse(customerDataStr) as Record<
          string,
          unknown
        > | null;
        const maybeId: unknown = obj
          ? (obj["customerId"] ?? obj["id"])
          : undefined;
        const parsedId = typeof maybeId === "number" ? maybeId : null;
        if (parsedId && parsedId > 0) {
          fieldStageCustomerIdRef.current = parsedId;
          return parsedId;
        }
      }
    } catch {
      // ignore
    }
    if (typeof user?.userId === "number" && user.userId > 0) {
      try {
        const resp = await OnboardingService.getCustomerByUserId(user.userId, {
          skipErrorCapture: true,
        });
        const data = resp?.data as Record<string, unknown> | undefined;
        const maybeId: unknown = data
          ? (data["customerId"] ?? data["id"])
          : undefined;
        const parsedId = typeof maybeId === "number" ? maybeId : null;
        if (parsedId && parsedId > 0) {
          fieldStageCustomerIdRef.current = parsedId;
          try {
            localStorage.setItem("customerData", JSON.stringify(data));
          } catch {}
          return parsedId;
        }
      } catch {
        // ignore
      }
    }
    return null;
  };

  // Auto-advance carousel every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Sync step with URL query param (?step=) and prevent forward jumps.
  // Users may only navigate forward up to the highest step they have
  // legitimately reached (tracked by lastPersistedStageRef from the backend).
  useEffect(() => {
    const paramStep = parseStepFromParams(searchParams);
    const clamped = clampStep(paramStep, totalSteps);
    let finalIndex = Math.max(clamped, earliestStep);

    // Prevent forward jumps beyond the user's persisted progress.
    // Back-navigation is always allowed; forward is capped.
    const maxAllowedStep =
      lastPersistedStageRef.current != null
        ? parseInt(lastPersistedStageRef.current, 10)
        : earliestStep;
    if (
      !Number.isNaN(maxAllowedStep) &&
      finalIndex > maxAllowedStep &&
      fieldStageInitializedRef.current
    ) {
      console.warn(
        `URL step ${finalIndex} exceeds progress (${maxAllowedStep}) — clamping`
      );
      finalIndex = maxAllowedStep;
    }

    if (finalIndex !== currentStep) {
      setCurrentStep(finalIndex);
    }
    if (finalIndex !== paramStep) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("step", String(finalIndex));
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, totalSteps, currentStep, earliestStep]);

  // If referral steps must be skipped, ensure we land on the first allowed step
  useEffect(() => {
    if (skipReferralSteps && currentStep < 2) {
      goToStep(2, { replace: true });
    }
  }, [skipReferralSteps, currentStep]);

  // Step prerequisite check: step 1 requires step 0 data (referral method selection).
  // Even if field stage allows step 1, we still need actual step 0 form data.
  useEffect(() => {
    if (currentStep !== 1) return;
    if (skipReferralSteps) return;
    const step0Data = allFormData["onboarding-step-0"] as
      | Record<string, unknown>
      | undefined;
    const hasReferralMethod =
      step0Data != null &&
      typeof step0Data.referedFrom === "string" &&
      step0Data.referedFrom.trim().length > 0;
    if (!hasReferralMethod) {
      console.warn(
        "Step 1 loaded without step 0 data — redirecting to step 0"
      );
      goToStep(0, { replace: true });
    }
  }, [currentStep, skipReferralSteps, allFormData]);

  // Step prerequisite check: vulnerability follow-up steps (8-10) require step 7
  // answer to be "yes". If the user jumps to these directly without step 7 approval,
  // redirect to step 7.
  useEffect(() => {
    if (currentStep < 8 || currentStep > 10) return;
    const step7Data = allFormData["onboarding-step-7"] as
      | Record<string, unknown>
      | undefined;
    const vulnerabilityAnswer =
      step7Data?.hasVulnerabilityCircumstances as string | undefined;
    if (vulnerabilityAnswer !== "yes") {
      console.warn(
        `Step ${currentStep} requires vulnerability = "yes" — redirecting to step 7`
      );
      goToStep(7, { replace: true });
    }
  }, [currentStep, allFormData]);

  // On very first render, proactively jump to step 2 when Creditor is present in persisted data
  useEffect(() => {
    if (currentStep < 2) {
      try {
        const raw = localStorage.getItem("customerData");
        if (raw) {
          const obj = JSON.parse(raw) as Record<string, unknown> | null;
          if (obj && (obj as Record<string, any>)?.textField2 === "Creditor") {
            goToStep(2, { replace: true });
          }
        }
      } catch {
        // ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist field stage whenever currentStep changes and we have a customer id
  useEffect(() => {
    if (fieldStageCustomerIdRef.current && fieldStageInitializedRef.current) {
      persistFieldStage(currentStep);
    }
  }, [currentStep]);

  // Redirect to dashboard only if progressing beyond the last step (index > 10)
  useEffect(() => {
    if (currentStep > 10) {
      navigate("/dashboard", { replace: true });
    }
  }, [currentStep, navigate]);

  // Load and apply customer prefill data on component mount
  useEffect(() => {
    if (!user?.userId || prefillLoaded) {
      return;
    }

    const loadPrefillData = async (): Promise<void> => {
      try {
        setLoading("prefill-load", true);

        const prefillResponse = await OnboardingService.loadCustomerPrefillData(
          user.userId,
          {
            skipErrorCapture: false,
          }
        );

        if (prefillResponse.success && prefillResponse.data) {
          let appliedAny = false;

          // Check if step 0 has a non-company referral method
          const step0Data = prefillResponse.data["onboarding-step-0"] as
            | Record<string, unknown>
            | undefined;
          const referMethod = step0Data?.referedFrom;
          const isCompanyReferral =
            referMethod === "Referred by a company" ||
            referMethod === "Debt charity or advice service";

          Object.entries(prefillResponse.data).forEach(
            ([stepKey, stepData]) => {
              // Skip prefilling step 1 if referral method is not company-related
              if (stepKey === "onboarding-step-1" && !isCompanyReferral) {
                console.log(
                  "🚫 Skipping step 1 prefill - non-company referral method"
                );
                return;
              }

              const existing =
                (allFormData[stepKey] as Record<string, unknown> | undefined) ||
                {};
              const merged: Record<string, unknown> = {
                ...stepData,
                ...existing,
              };
              const changed = Object.keys(stepData).some(
                (k) => existing[k] === undefined
              );
              if (changed) {
                appliedAny = true;
                setFormData(stepKey, merged);
              }
            }
          );

          // After prefill, attempt to restore last field stage and load customer/debts data
          try {
            const customerDataStr = localStorage.getItem("customerData");
            const customerData = customerDataStr
              ? (JSON.parse(customerDataStr) as Record<string, any>)
              : null;
            setCustomerData(customerData);

            const effectiveCustomerId = (customerData?.customerId ??
              customerData?.id) as number | undefined;
            if (
              typeof effectiveCustomerId === "number" &&
              effectiveCustomerId > 0
            ) {
              fieldStageCustomerIdRef.current = effectiveCustomerId;

              // Load debts data for field disabling conditions
              try {
                const debts = await OnboardingService.getDebts(
                  effectiveCustomerId,
                  { skipErrorCapture: true }
                );
                setDebtsData(Array.isArray(debts) ? debts : []);
              } catch {
                setDebtsData([]);
              }

              const stageResp = await OnboardingService.getCustomerFieldStage(
                effectiveCustomerId,
                { skipErrorCapture: true }
              );
              if (stageResp && typeof stageResp.fieldStage === "string") {
                fieldStageRecordIdRef.current =
                  typeof stageResp.id === "number" ? stageResp.id : null;
                const parsed = parseInt(stageResp.fieldStage, 10);
                if (!Number.isNaN(parsed)) {
                  fieldStageInitializedRef.current = true;
                  if (parsed > 10) {
                    // If user previously completed or skipped beyond vulnerability, send to dashboard
                    lastPersistedStageRef.current = String(parsed);
                    navigate("/dashboard", { replace: true });
                  } else {
                    // Clamp based on current computed totalSteps snapshot (base flow first)
                    const baseTotal = onboardingSchemas.length;
                    const clamped = clampStep(parsed, baseTotal);
                    goToStep(clamped, {
                      replace: true,
                      assumeTotalSteps: baseTotal,
                    });
                    lastPersistedStageRef.current = String(clamped);
                  }
                }
              } else {
                // No existing stage; initialize at step 0 by posting after first submit
                fieldStageInitializedRef.current = true;
                goToStep(0, {
                  replace: true,
                  assumeTotalSteps: onboardingSchemas.length,
                });
                lastPersistedStageRef.current = "0";
              }
            }
          } catch {
            // ignore stage restore errors
          }

          if (appliedAny) {
            addAlert({
              severity: "success",
              message:
                "We pre-filled some details from your last session. Please review and continue.",
              autoHide: true,
              duration: 4000,
            });
            console.log(
              "Prefill data applied successfully:",
              prefillResponse.data
            );
          } else {
            console.log("Prefill fetched, but no changes were needed.");
          }
        } else {
          console.log("No existing customer data found for prefill");
        }

        setPrefillLoaded(true);
      } catch (error) {
        console.error("Failed to load prefill data:", error);

        addAlert({
          severity: "warning",
          message:
            "Could not load your previous information. You can continue with the form.",
          autoHide: true,
          duration: 3000,
        });

        setPrefillLoaded(true);
      } finally {
        setLoading("prefill-load", false);
      }
    };

    void loadPrefillData();
  }, [
    user?.userId,
    prefillLoaded,
    allFormData,
    setFormData,
    addAlert,
    setLoading,
  ]);

  useEffect(() => {
    if (!prefillLoaded) {
      return;
    }

    if (emailPrefillAttempted) {
      return;
    }

    const userId = user?.userId;
    if (typeof userId !== "number" || userId <= 0) {
      return;
    }

    const contactStepKey = "onboarding-step-5";
    const contactStepData =
      (allFormData[contactStepKey] as Record<string, unknown> | undefined) ??
      {};
    const existingEmail =
      typeof contactStepData.emailId === "string"
        ? contactStepData.emailId.trim()
        : "";
    if (existingEmail.length > 0) {
      setEmailPrefillAttempted(true);
      return;
    }

    let cancelled = false;

    const resolveEmail = async (): Promise<void> => {
      try {
        const customerResponse = await OnboardingService.getCustomerByUserId(
          userId,
          { skipErrorCapture: false }
        );
        const emailCandidate = (
          (customerResponse.success && customerResponse.data
            ? (customerResponse.data.emailAddress as string | undefined)
            : undefined) ??
          user?.emailAddress ??
          ""
        ).trim();

        if (!cancelled && emailCandidate.length > 0) {
          const latestData =
            (allFormData[contactStepKey] as
              | Record<string, unknown>
              | undefined) ?? {};
          setFormData(contactStepKey, {
            ...latestData,
            emailId: emailCandidate,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to prefill email from customer record", error);
          const fallbackEmail =
            typeof user?.emailAddress === "string"
              ? user.emailAddress.trim()
              : "";
          if (fallbackEmail.length > 0) {
            const latestData =
              (allFormData[contactStepKey] as
                | Record<string, unknown>
                | undefined) ?? {};
            setFormData(contactStepKey, {
              ...latestData,
              emailId: fallbackEmail,
            });
          }
        }
      } finally {
        if (!cancelled) {
          setEmailPrefillAttempted(true);
        }
      }
    };

    void resolveEmail();

    return () => {
      cancelled = true;
    };
  }, [
    prefillLoaded,
    emailPrefillAttempted,
    allFormData,
    setFormData,
    user?.userId,
    user?.emailAddress,
  ]);

  // Define fallback options and constants first (before they're used)
  const referedFromCompanySection = (referedFromCompany as any)?.sections?.[0];
  const referedFromCompanyField = referedFromCompanySection?.fields?.find(
    (field: any) => field.fieldName === "referedFromCompany"
  );
  const fallbackCompanyOptions: CompanySelectOption[] = useMemo(
    () =>
      Array.isArray(referedFromCompanyField?.options)
        ? referedFromCompanyField.options.map(
            (option: {
              value: string;
              label: string;
              childCompany?: string[];
              showCompanyList?: boolean;
              referenceNumberFormat?: string;
            }) => ({
              value: String(option?.value ?? option?.label ?? "").trim(),
              label: String(option?.label ?? option?.value ?? "").trim(),
              childCompany: Array.isArray(option?.childCompany)
                ? option.childCompany
                    .map((child) => String(child).trim())
                    .filter(Boolean)
                : [],
              showCompanyList: option?.showCompanyList === true,
              ...(option?.referenceNumberFormat !== undefined && {
                referenceNumberFormat: option.referenceNumberFormat,
              }),
            })
          )
        : [],
    [referedFromCompanyField]
  );

  const fallbackLenders: LenderParentRecord[] = useMemo(
    () =>
      fallbackCompanyOptions.map(
        (option: CompanySelectOption, index: number) => ({
          id: -(index + 1),
          parentCompany: option.label,
          description: option.label,
          showCompanyList: false,
          referralCompanies: false,
          customerPaymentOffer: false,
          addToDebtList: false,
          addToShareListFromProfile: false,
          childCompany: option.childCompany ?? [],
          ...(option.referenceNumberFormat !== undefined && {
            referenceNumberFormat: option.referenceNumberFormat,
          }),
          raw: {
            id: -(index + 1),
            lender: option.label,
            parentCompany: option.label,
            showCompanyList: option.showCompanyList ? "Y" : "N",
            referralCompanies: "N",
            customerPaymentOffer: "N",
            addToDebtList: "N",
            addToShareListFromProfile: "N",
            ...(option.referenceNumberFormat !== undefined && {
              referenceNumberFormat: option.referenceNumberFormat,
            }),
          },
        })
      ),
    [fallbackCompanyOptions]
  );

  const allLenders = useMemo<LenderParentRecord[]>(
    () => (lenders.length > 0 ? lenders : fallbackLenders),
    [lenders, fallbackLenders]
  );

  const lenderRecordLookup = useMemo<Map<string, LenderParentRecord>>(() => {
    const map = new Map<string, LenderParentRecord>();
    allLenders.forEach((record) => {
      const names = new Set<string>();
      if (typeof record.parentCompany === "string") {
        names.add(normalizeString(record.parentCompany));
      }
      if (typeof record.description === "string") {
        names.add(normalizeString(record.description));
      }
      const lenderName = (record as { lender?: string }).lender;
      if (typeof lenderName === "string") {
        names.add(normalizeString(lenderName));
      }
      names.forEach((name) => {
        if (name) {
          map.set(name, record);
        }
      });
    });
    return map;
  }, [allLenders]);

  const referedFromCompanyStepData = useMemo(
    () =>
      allFormData["onboarding-step-1"] as Record<string, unknown> | undefined,
    [allFormData]
  );

  const referMethodStepData = useMemo(
    () =>
      allFormData["onboarding-step-0"] as Record<string, unknown> | undefined,
    [allFormData]
  );

  const referMethodSelection = useMemo(() => {
    const raw = getString(referMethodStepData, "referedFrom");
    const trimmed = typeof raw === "string" ? raw.trim() : "";
    return trimmed.length > 0 ? trimmed : undefined;
  }, [referMethodStepData]);

  const requiresCompanySelection = useMemo(
    () =>
      referMethodSelection === "Referred by a company" ||
      referMethodSelection === "Debt charity or advice service",
    [referMethodSelection]
  );

  const isNotReferredByCompany = useMemo(() => {
    if (typeof notReferredByCompanyLive === "boolean") {
      return notReferredByCompanyLive;
    }
    return (
      getBoolean(referedFromCompanyStepData, "notReferredByCompany") === true
    );
  }, [notReferredByCompanyLive, referedFromCompanyStepData]);

  const referedFromCompanyValue = useMemo(() => {
    const storedValue =
      getString(referedFromCompanyStepData, "referedFromCompany") ?? "";
    // When "Not Referred By a Company" is checked, ignore any live company selection
    const effectiveValue =
      currentStep === 1 && !isNotReferredByCompany
        ? (companySelectionLive ?? storedValue)
        : storedValue;
    return typeof effectiveValue === "string" ? effectiveValue.trim() : "";
  }, [
    referedFromCompanyStepData,
    companySelectionLive,
    currentStep,
    isNotReferredByCompany,
  ]);

  const filteredLenders = useMemo<LenderParentRecord[]>(() => {
    const base = allLenders;

    const sanitizedBase = base.filter((record) => {
      const parentName =
        typeof record.parentCompany === "string"
          ? normalizeString(record.parentCompany)
          : "";
      const lenderName =
        typeof (record as { lender?: string }).lender === "string"
          ? normalizeString((record as { lender?: string }).lender)
          : "";
      const descriptionName =
        typeof record.description === "string"
          ? normalizeString(record.description)
          : "";
      return (
        !hiddenCompanyNameSet.has(parentName) &&
        !hiddenCompanyNameSet.has(lenderName) &&
        !hiddenCompanyNameSet.has(descriptionName) &&
        // Legacy parity: Barclays Money Management is only visible for pre-arrears users
        (isPrearrears ||
          (parentName !== BARCLAYS_MM_KEY &&
            lenderName !== BARCLAYS_MM_KEY &&
            descriptionName !== BARCLAYS_MM_KEY))
      );
    });

    // Show the full list for both company and debt-charity referrals; other
    // methods already fall back to the sanitized list.
    return sanitizedBase;
  }, [allLenders, referMethodSelection, isPrearrears]);

  const bumpFormInstanceKey = useCallback((index: number) => {
    setFormInstanceKeys((prev) => ({
      ...prev,
      [index]: (prev[index] ?? 0) + 1,
    }));
  }, []);

  // Barclays pre-arrears: auto-select the referred company and keep it locked.
  // Also ensure step-0 is set to a company referral when empty, so the flow remains consistent.
  useEffect(() => {
    if (!isPrearrears) return;

    // Step 0: refer method (only set if missing)
    const step0 = allFormData["onboarding-step-0"] as
      | Record<string, unknown>
      | undefined;
    const referedFrom0 = (getString(step0, "referedFrom") ?? "").trim();
    if (!referedFrom0) {
      setFormData("onboarding-step-0", {
        ...(step0 ?? {}),
        referedFrom: "Referred by a company",
      });
      bumpFormInstanceKey(0);
    }

    // Step 1: referred company (only set if not already set and not explicitly \"not referred\")
    const step1 = allFormData["onboarding-step-1"] as
      | Record<string, unknown>
      | undefined;
    const notReferred = getBoolean(step1, "notReferredByCompany") === true;
    if (notReferred) return;

    const referedFromCompany = (
      getString(step1, "referedFromCompany") ?? ""
    ).trim();
    if (!referedFromCompany) {
      setFormData("onboarding-step-1", {
        ...(step1 ?? {}),
        referedFromCompany: BARCLAYS_MM_LENDER_NAME,
        otherCompanyName: "",
        productName: "",
        accountType: "",
        referenceId: "",
        noReferenceId: false,
        notReferredByCompany: false,
      });

      // Ensure dynamic schema derives from the forced selection when step 1 is active.
      if (currentStep === 1) {
        setCompanySelectionLive(BARCLAYS_MM_LENDER_NAME);
        setNotReferredByCompanyLive(false);
        setProductSelectionLive(undefined);
        setAccountTypeOptions([]);
        setAccountTypeCompanyKey(null);
        autoSelectedAccountTypeRef.current = null;
      }

      bumpFormInstanceKey(1);
    }
  }, [
    isPrearrears,
    allFormData,
    setFormData,
    bumpFormInstanceKey,
    currentStep,
  ]);

  // Defer remount to avoid interfering with open popovers/menus; also blur active element first
  const remountStep1Form = useCallback(() => {
    try {
      const activeEl =
        typeof document !== "undefined"
          ? (document.activeElement as HTMLElement | null)
          : null;
      if (activeEl && typeof activeEl.blur === "function") {
        activeEl.blur();
      }
    } catch {
      // ignore
    }
    // Give portals/menus time to unmount before remounting the form
    setTimeout(() => {
      bumpFormInstanceKey(1);
    }, 120);
  }, [bumpFormInstanceKey]);

  const resetCompanyStepFields = useCallback(() => {
    const existingData = referedFromCompanyStepData;
    if (existingData == null) {
      return;
    }

    const nextData: Record<string, unknown> = {
      ...existingData,
      referedFromCompany: "",
      otherCompanyName: "",
      productName: "",
      accountType: "",
      referenceId: "",
    };

    setCompanySelectionLive(undefined);
    setNotReferredByCompanyLive(undefined);
    setAccountTypeOptions([]);
    setAccountTypeCompanyKey(null);
    setFormData("onboarding-step-1", nextData);
    bumpFormInstanceKey(1);
  }, [referedFromCompanyStepData, setFormData, bumpFormInstanceKey]);

  // Clear step 1 data when referral method changes to non-company option
  useEffect(() => {
    const step0Data = allFormData["onboarding-step-0"];
    const step1Data = allFormData["onboarding-step-1"];
    const referMethod = isRecord(step0Data) ? step0Data.referedFrom : undefined;

    // Only clear if not already cleared and if reset is needed
    if (
      !step1ClearedRef.current &&
      shouldResetCompanyStepForReferral(referMethod, step1Data)
    ) {
      step1ClearedRef.current = true;

      // Clear frontend state
      setFormData("onboarding-step-1", {});
      setSchemaOverrides((prev) => {
        if (!prev[1]) {
          return prev;
        }
        const next = { ...prev };
        delete next[1];
        return next;
      });
      setCompanySelectionLive(undefined);
      setProductSelectionLive(undefined);
      setNotReferredByCompanyLive(undefined);
      setAccountTypeOptions([]);
      setAccountTypeCompanyKey(null);
      autoSelectedAccountTypeRef.current = null;

      // Clear backend data asynchronously (will also be done synchronously on submit)
      void (async () => {
        try {
          const ensuredCustomerId =
            fieldStageCustomerIdRef.current ??
            (await ensureFieldStageCustomerId());
          const customerIdVal =
            typeof ensuredCustomerId === "number" ? ensuredCustomerId : null;
          if (!customerIdVal || !user?.userId) {
            return;
          }

          try {
            const debts = await OnboardingService.getDebts(customerIdVal, {
              skipErrorCapture: true,
            });
            if (Array.isArray(debts) && debts.length > 0) {
              await Promise.all(
                debts.map((debt) => {
                  const debtId =
                    typeof debt?.id === "number" ? debt.id : undefined;
                  if (!debtId) {
                    return undefined;
                  }
                  return OnboardingService.deleteDebts(customerIdVal, debtId, {
                    skipErrorCapture: true,
                  });
                })
              );
            }
          } catch {
            // Debt cleanup failure is non-blocking
          }

          try {
            const recordCandidate = isRecord(customerData)
              ? customerData.id
              : undefined;
            const customerIdCandidate = isRecord(customerData)
              ? customerData.customerId
              : undefined;
            const resolvedRecordId =
              typeof recordCandidate === "number"
                ? recordCandidate
                : customerIdVal;
            const resolvedCustomerId =
              typeof customerIdCandidate === "number"
                ? customerIdCandidate
                : customerIdVal;

            await OnboardingService.updateCustomer(
              {
                ...allFormData,
                "onboarding-step-1": {},
              },
              user.userId,
              resolvedRecordId ?? customerIdVal,
              resolvedCustomerId ?? customerIdVal,
              typeof user.emailAddress === "string" ? user.emailAddress : "",
              { skipErrorCapture: true }
            );
          } catch {
            // Backend clearing failure is non-blocking
          }
        } catch {
          // silently ignore cleanup errors
        }
      })();
    } else if (!shouldResetCompanyStepForReferral(referMethod, step1Data)) {
      // Reset the flag when user selects a company referral method again
      step1ClearedRef.current = false;
    }
  }, [
    allFormData,
    setFormData,
    user?.userId,
    user?.emailAddress,
    customerData,
    ensureFieldStageCustomerId,
  ]);

  useEffect(() => {
    const currentValue = referedFromCompanyValue;
    if (!currentValue || currentValue === OTHER_COMPANY_OPTION.value) {
      return;
    }

    const hasMatch = filteredLenders.some(
      (record) => record.parentCompany.trim() === currentValue
    );
    if (hasMatch) {
      return;
    }

    const normalizedValue = normalizeString(currentValue);
    if (lenderRecordLookup.has(normalizedValue)) {
      return;
    }

    resetCompanyStepFields();
  }, [
    filteredLenders,
    referedFromCompanyValue,
    resetCompanyStepFields,
    lenderRecordLookup,
  ]);

  useEffect(() => {
    if (!requiresCompanySelection) {
      if (
        referedFromCompanyValue ||
        isNotReferredByCompany ||
        companySelectionLive
      ) {
        resetCompanyStepFields();
      }
      return;
    }

    const currentValue = referedFromCompanyValue;
    if (!currentValue || currentValue === OTHER_COMPANY_OPTION.value) {
      return;
    }

    const matchedRecord = findLenderByName(
      lenderRecordLookup,
      allLenders,
      currentValue
    );
    if (!matchedRecord) {
      resetCompanyStepFields();
    }
  }, [
    requiresCompanySelection,
    referedFromCompanyValue,
    filteredLenders,
    isNotReferredByCompany,
    companySelectionLive,
    resetCompanyStepFields,
    lenderRecordLookup,
  ]);

  // Compute company options from lenders
  const companyOptions = useMemo<CompanySelectOption[]>(() => {
    const source = filteredLenders;
    const unique = new Map<string, CompanySelectOption>();

    source.forEach((record) => {
      const parentLabel = record.parentCompany.trim();
      if (!parentLabel || parentLabel === OTHER_COMPANY_OPTION.value) {
        return;
      }

      const childSet = new Set(
        (record.childCompany ?? []).map((child) => child.trim()).filter(Boolean)
      );
      const refFormat =
        record.referenceNumberFormat ?? record.raw?.referenceNumberFormat;
      unique.set(parentLabel, {
        value: parentLabel,
        label: parentLabel,
        childCompany: Array.from(childSet),
        ...(refFormat !== undefined && { referenceNumberFormat: refFormat }),
        showCompanyList: childSet.size > 0,
      });
    });

    // Also include the currently selected company if it's not already in the list
    const currentValue = referedFromCompanyValue;
    if (
      currentValue &&
      currentValue !== OTHER_COMPANY_OPTION.value &&
      !unique.has(currentValue)
    ) {
      const normalizedValue = normalizeString(currentValue);
      const record = lenderRecordLookup.get(normalizedValue);
      if (record) {
        const parentLabel = record.parentCompany.trim();
        const childSet = new Set(
          (record.childCompany ?? [])
            .map((child) => child.trim())
            .filter(Boolean)
        );
        const refFormat =
          record.referenceNumberFormat ?? record.raw?.referenceNumberFormat;
        unique.set(parentLabel, {
          value: parentLabel,
          label: parentLabel,
          childCompany: Array.from(childSet),
          ...(refFormat !== undefined && { referenceNumberFormat: refFormat }),
          showCompanyList: childSet.size > 0,
        });
      }
    }

    const sorted = Array.from(unique.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return [OTHER_COMPANY_OPTION, ...sorted];
  }, [filteredLenders, referedFromCompanyValue, lenderRecordLookup]);

  // Compute selected company
  const selectedCompany: LenderParentRecord | null = useMemo(() => {
    const value = referedFromCompanyValue;
    if (!value || value === OTHER_COMPANY_OPTION.value) {
      return null;
    }

    const record = findLenderByName(lenderRecordLookup, allLenders, value);

    if (record) {
      return record;
    }

    // Fallback company with no child companies
    return {
      id: -1,
      parentCompany: value,
      description: value,
      showCompanyList: false,
      referralCompanies: false,
      customerPaymentOffer: false,
      addToDebtList: false,
      addToShareListFromProfile: false,
      childCompany: [],
      raw: {
        id: -1,
        lender: value,
        showCompanyList: "N",
        referralCompanies: "N",
        customerPaymentOffer: "N",
        addToDebtList: "N",
        addToShareListFromProfile: "N",
      },
    };
  }, [referedFromCompanyValue, lenderRecordLookup, allLenders]);

  useEffect(() => {
    if (lendersLoaded) {
      return;
    }

    const loadLenders = async (): Promise<void> => {
      try {
        setLoading("lenders-load", true);
        const response = await LenderService.fetchLenders({
          skipErrorCapture: false,
        });
        if (response.success && Array.isArray(response.data)) {
          setLenders(response.data);
          setLendersLoaded(true);
        } else {
          setLenders(fallbackLenders);
          setLendersLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load lenders:", error);
        setLenders(fallbackLenders);
        setLendersLoaded(true);
      } finally {
        setLoading("lenders-load", false);
      }
    };

    void loadLenders();
  }, [lendersLoaded, setLoading, fallbackLenders]);

  useEffect(() => {
    if (currentStep !== 1) {
      // Reset live state when not on step 1
      if (companySelectionLive !== undefined) {
        setCompanySelectionLive(undefined);
      }
      if (notReferredByCompanyLive !== undefined) {
        setNotReferredByCompanyLive(undefined);
      }
      if (productSelectionLive !== undefined) {
        setProductSelectionLive(undefined);
      }
      if (accountTypeOptions.length > 0) {
        setAccountTypeOptions([]);
      }
      if (accountTypeCompanyKey !== null) {
        setAccountTypeCompanyKey(null);
      }
      lastCompanySelectionRef.current = undefined;
      return;
    }

    // Merge live values with persisted step data
    // Use the same value as the memoized referedFromCompanyValue for consistency
    const selectedValue = referedFromCompanyValue;
    const notReferredByCompany = isNotReferredByCompany;
    const shouldDisable = shouldDisableFields(customerData, debtsData, {
      hasProfileDebt,
    });
    const prearrearsLocked = isPrearrears;

    const overrideSchema: ScreenSchema | undefined = (() => {
      const baseSchema = referedFromCompany as unknown as ScreenSchema;
      const section = baseSchema.sections?.[0];
      if (!section) return undefined;

      const fields = section.fields?.map((field) => {
        if (field.fieldName === "referedFromCompany") {
          const nextUi = field.ui ?? {};
          const barclaysOnlyOptions: CompanySelectOption[] = [
            {
              value: BARCLAYS_MM_LENDER_NAME,
              label: BARCLAYS_MM_LENDER_NAME,
              childCompany: [],
              showCompanyList: false,
            },
          ];
          return {
            ...field,
            options: prearrearsLocked ? barclaysOnlyOptions : companyOptions,
            isEditable: !(shouldDisable || prearrearsLocked),
            isReadOnly: shouldDisable || prearrearsLocked,
            ui: {
              ...nextUi,
              helperTop: prearrearsLocked
                ? "Your referring company is Barclays Money Management."
                : (nextUi.helperTop ??
                  "Select your referring company or choose 'Can't find my referring company'"),
            },
          };
        }

        if (field.fieldName === "otherCompanyName") {
          if (prearrearsLocked) {
            return {
              ...field,
              isHidden: true,
              isRequired: false,
              isEditable: false,
              isReadOnly: true,
            };
          }
          const shouldShowOtherCompany =
            !notReferredByCompany &&
            selectedValue === OTHER_COMPANY_OPTION.value;
          return {
            ...field,
            isHidden: !shouldShowOtherCompany,
            isRequired: shouldShowOtherCompany,
            isEditable: true,
            isReadOnly: false,
          };
        }

        if (field.fieldName === "notReferredByCompany") {
          if (prearrearsLocked) {
            return {
              ...field,
              isHidden: true,
              isRequired: false,
              isEditable: false,
              isReadOnly: true,
            };
          }
          return field;
        }

        if (field.fieldName === "productName") {
          if (prearrearsLocked) {
            return {
              ...field,
              isHidden: true,
              isRequired: false,
              isEditable: false,
              isReadOnly: true,
            };
          }
          const childCompanies = selectedCompany?.childCompany ?? [];
          const hasChildCompanies = childCompanies.length > 0;
          const customerDebt = debtsData?.[0];
          const showFromDebt = customerDebt?.parentCompany === "Y";
          const showFromFlags =
            isAffirmative(selectedCompany?.showCompanyList) ||
            isAffirmative(
              (selectedCompany?.raw as Record<string, unknown> | undefined)
                ?.showCompanyList
            );
          const productOptions = childCompanies.map((child) => ({
            value: child,
            label: child,
          }));
          const hasOptions = productOptions.length > 0;

          // Check if user has already selected a product (from any source)
          const liveRefProduct = getString(
            liveStepValuesRef.current,
            "productName"
          );
          const persistedProduct = getString(
            referedFromCompanyStepData,
            "productName"
          );
          const currentProductValue =
            productSelectionLive || liveRefProduct || persistedProduct;
          const hasExistingProduct = Boolean(
            currentProductValue && String(currentProductValue).trim().length > 0
          );

          // Product field visibility logic:
          // Show if: company requires product selection AND has options
          // OR if user already selected a product (don't hide if they saved data)
          const companyRequiresProduct =
            hasChildCompanies || showFromDebt || showFromFlags;
          const hasCompanySelection = Boolean(selectedValue);
          const shouldShowProduct =
            !notReferredByCompany &&
            hasCompanySelection &&
            selectedValue !== OTHER_COMPANY_OPTION.value &&
            ((companyRequiresProduct && hasOptions) ||
              (hasExistingProduct && hasOptions));

          return {
            ...field,
            isHidden: !shouldShowProduct,
            isRequired: shouldShowProduct,
            isEditable: !shouldDisable,
            isReadOnly: shouldDisable,
            options: productOptions,
          };
        }

        if (field.fieldName === "accountType") {
          if (prearrearsLocked) {
            return {
              ...field,
              isHidden: true,
              isRequired: false,
              isEditable: false,
              isReadOnly: true,
              options: [],
            };
          }
          const hasSelection = !notReferredByCompany && Boolean(selectedValue);
          const options = accountTypeOptions.map((item) => ({
            value: item.code,
            label: item.label,
          }));
          const uniqueAccountTypeCodes = Array.from(
            new Set(
              options
                .map((opt) => opt.value)
                .filter(
                  (value) => value != null && String(value).trim().length > 0
                )
            )
          );
          const hasSingleAccountType = uniqueAccountTypeCodes.length === 1;

          // Check if this is "Can't find my referring company" selection
          const isOtherCompany = selectedValue === OTHER_COMPANY_OPTION.value;

          // For OTHER_COMPANY, always show account type if we have options
          // For regular companies, only show if addToDebtList is Y
          const addToDebtListY =
            isAffirmative(selectedCompany?.addToDebtList) ||
            isAffirmative(
              (selectedCompany?.raw as Record<string, unknown> | undefined)
                ?.addToDebtList
            );
          const shouldShowAccountType =
            hasSelection &&
            (isOtherCompany || addToDebtListY) &&
            options.length > 0 &&
            !hasSingleAccountType;
          const accountTypeValue =
            getString(referedFromCompanyStepData, "accountType") ?? "";

          return {
            ...field,
            isHidden: !shouldShowAccountType,
            isRequired: shouldShowAccountType,
            isEditable: !shouldDisable && shouldShowAccountType,
            isReadOnly: shouldDisable || !shouldShowAccountType,
            options: shouldShowAccountType ? options : [],
            defaultValue: hasSingleAccountType
              ? accountTypeValue
              : field.defaultValue,
          };
        }

        if (field.fieldName === "referenceId") {
          if (prearrearsLocked) {
            return {
              ...field,
              isHidden: true,
              isRequired: false,
              isEditable: false,
              isReadOnly: true,
            };
          }
          // Check all possible sources for product value to ensure we don't lose it during re-renders
          // Priority: live selection > ref value > persisted form data
          const liveRefProduct =
            getString(liveStepValuesRef.current, "productName") ?? "";
          const storedProductValue =
            getString(referedFromCompanyStepData, "productName") ?? "";

          // Use the same logic as product field to determine effective value
          const effectiveProductValue =
            productSelectionLive || liveRefProduct || storedProductValue;
          const productValue =
            typeof effectiveProductValue === "string"
              ? effectiveProductValue.trim()
              : "";
          const compositeKey = selectedCompany
            ? `${normalizeString(selectedCompany.parentCompany)}|${normalizeString(productValue)}`
            : undefined;

          // Try multiple lookup strategies for the product
          let productCompanyRecord = findLenderByName(
            lenderRecordLookup,
            allLenders,
            productValue
          );

          // If not found and we have a composite key, try that
          if (!productCompanyRecord && compositeKey) {
            productCompanyRecord = findLenderByName(
              lenderRecordLookup,
              allLenders,
              compositeKey
            );
          }

          // If still not found, check if productValue is one of the selectedCompany's child companies
          // In that case, use a synthetic record with the child's attributes
          if (!productCompanyRecord && selectedCompany && productValue) {
            const childCompanies = selectedCompany.childCompany ?? [];
            const normalizedProduct = normalizeString(productValue);
            const isChildCompany = childCompanies.some(
              (child) => normalizeString(child) === normalizedProduct
            );

            if (isChildCompany) {
              // Product is a child company - check if parent has addToDebtList
              // For child companies, inherit addToDebtList from parent if product record not found
              const parentRaw = selectedCompany.raw;
              const parentAddToDebtList =
                isAffirmative(selectedCompany.addToDebtList) ||
                (parentRaw && typeof parentRaw === "object"
                  ? isAffirmative(
                      (parentRaw as unknown as Record<string, unknown>)
                        .addToDebtList
                    )
                  : false);

              // Create synthetic record for the child product
              if (parentAddToDebtList) {
                productCompanyRecord = {
                  ...selectedCompany,
                  description: productValue,
                  parentCompany: selectedCompany.parentCompany,
                  addToDebtList: true,
                };
              }
            }
          }

          const effectiveCompany = productCompanyRecord ?? selectedCompany;

          const referencePlaceholder =
            productCompanyRecord?.referenceNumberFormat ??
            effectiveCompany?.referenceNumberFormat ??
            field.ui?.placeholder ??
            "";
          const childCompanies =
            effectiveCompany?.childCompany ??
            selectedCompany?.childCompany ??
            [];
          const normalizedCompanySelection = normalizeString(selectedValue);
          const normalizedProductSelection = normalizeString(productValue);
          const companyNameKey = normalizeString(
            effectiveCompany?.parentCompany ?? selectedValue
          );
          const normalizedOther = normalizeString(
            "I have not been referred by a company"
          );
          const specialNotReferredValues = new Set([
            normalizedOther,
            normalizeString("Not referred by a company"),
          ]);
          const cadentKeys = new Set([
            normalizeString("Cadent Gas"),
            normalizeString("Cadent Gas Network"),
            normalizeString("Cadent Gas Network 123"),
            normalizeString("Cadent Gas Network 456"),
          ]);
          const blockedCompanyKeys = new Set([
            normalizeString("CAB"),
            normalizeString("Reach Out"),
            ...cadentKeys,
          ]);
          const isNotReferredSpecial = specialNotReferredValues.has(
            normalizedCompanySelection
          );
          const isOtherCompanySelection =
            normalizedCompanySelection ===
            normalizeString(OTHER_COMPANY_OPTION.value);
          const effectiveRaw = effectiveCompany?.raw as
            | Record<string, unknown>
            | undefined;
          const candidateBlockedKeys = [
            companyNameKey,
            normalizedCompanySelection,
            normalizedProductSelection,
            normalizeString(
              (effectiveCompany as { description?: string } | null | undefined)
                ?.description
            ),
            normalizeString(
              (effectiveCompany as { lender?: string } | null | undefined)
                ?.lender
            ),
            normalizeString(effectiveRaw?.parentCompany),
            normalizeString(effectiveRaw?.lender),
          ];
          const isBlockedCompany = candidateBlockedKeys.some(
            (key) => key && blockedCompanyKeys.has(key)
          );
          const requiresProductSelection = childCompanies.length > 0;
          const hasProductSelection = requiresProductSelection
            ? Boolean(productValue)
            : true;
          const hasCompanySelection = Boolean(selectedValue);
          const addToDebtListY =
            isAffirmative(productCompanyRecord?.addToDebtList) ||
            isAffirmative(
              (productCompanyRecord?.raw as Record<string, unknown> | undefined)
                ?.addToDebtList
            ) ||
            isAffirmative(effectiveCompany?.addToDebtList) ||
            isAffirmative(effectiveRaw?.addToDebtList);

          const showCompanyListY =
            isAffirmative(productCompanyRecord?.showCompanyList) ||
            isAffirmative(
              (productCompanyRecord?.raw as Record<string, unknown> | undefined)
                ?.showCompanyList
            ) ||
            isAffirmative(effectiveCompany?.showCompanyList) ||
            isAffirmative(effectiveRaw?.showCompanyList);

          // If a specific product (child company) has been selected, prefer that child's flags
          let childShowCompanyListY: boolean | undefined;
          let childAddToDebtListY: boolean | undefined;
          if (
            productValue &&
            selectedCompany &&
            Array.isArray((selectedCompany as any).childrenRaw)
          ) {
            const childrenRaw =
              (
                selectedCompany as unknown as {
                  childrenRaw?: Array<Record<string, unknown>>;
                }
              ).childrenRaw ?? [];
            const childRawMatch = childrenRaw.find((child) => {
              const childLender = normalizeString(
                (child as { lender?: string }).lender
              );
              const childDesc = normalizeString(
                (child as { description?: string }).description
              );
              return (
                childLender === normalizedProductSelection ||
                childDesc === normalizedProductSelection
              );
            });
            if (childRawMatch) {
              childShowCompanyListY = isAffirmative(
                (childRawMatch as { showCompanyList?: unknown }).showCompanyList
              );
              childAddToDebtListY = isAffirmative(
                (childRawMatch as { addToDebtList?: unknown }).addToDebtList
              );
            }
          }

          const effectiveShowCompanyListY =
            childShowCompanyListY !== undefined
              ? childShowCompanyListY
              : showCompanyListY;
          const effectiveAddToDebtListY =
            childAddToDebtListY !== undefined
              ? childAddToDebtListY
              : addToDebtListY;

          const meetsCommonConditions =
            !notReferredByCompany &&
            hasCompanySelection &&
            hasProductSelection &&
            !isNotReferredSpecial;

          const shouldShowReference = (() => {
            if (!meetsCommonConditions) {
              return false;
            }
            if (isOtherCompanySelection) {
              return true;
            }
            if (isBlockedCompany) {
              return false;
            }
            return effectiveAddToDebtListY && effectiveShowCompanyListY;
          })();

          // Keep existing validation rules but ensure they don't block when checkbox is checked
          const validationRules = field.validationRules
            ? [...field.validationRules]
            : [];

          return {
            ...field,
            isHidden: !shouldShowReference,
            isEditable: true,
            isReadOnly: false,
            // Always set isRequired to true so the field is validated
            // The requiredWhen will control whether validation actually enforces the requirement
            isRequired: true,
            // Add conditional requirement that form engine will respect
            // Field is required only when checkbox is NOT checked
            requiredWhen: shouldShowReference
              ? {
                  op: "ne",
                  args: [{ var: "noReferenceId" }, true],
                }
              : null,
            requiredErrorMessage: "Please enter your reference number",
            validationRules: validationRules,
            ui: {
              ...field.ui,
              placeholder: referencePlaceholder,
            },
            ...(field.options !== undefined && { options: field.options }),
          };
        }

        return field;
      });

      return {
        ...baseSchema,
        sections: [
          {
            ...section,
            fields,
          },
        ],
      };
    })();

    if (overrideSchema) {
      try {
        const nextJson = JSON.stringify(overrideSchema);
        if (nextJson !== lastSchemaOverrideJsonRef.current) {
          lastSchemaOverrideJsonRef.current = nextJson;
          setSchemaOverrides((prev) => ({ ...prev, 1: overrideSchema }));
        }
      } catch {
        setSchemaOverrides((prev) => ({ ...prev, 1: overrideSchema }));
      }
    }
  }, [
    currentStep,
    companyOptions,
    selectedCompany,
    companySelectionLive,
    notReferredByCompanyLive,
    isNotReferredByCompany,
    productSelectionLive,
    accountTypeOptions,
    accountTypeCompanyKey,
    referedFromCompanyValue,
    referedFromCompanyStepData,
    customerData,
    debtsData,
    setFormData,
    lenderRecordLookup,
    allLenders,
    lendersLoaded,
    hasProfileDebt,
  ]);

  // When user marks "Not Referred By a Company", aggressively clear step-1 live selections and options and reset validation state
  useEffect(() => {
    if (currentStep !== 1) {
      return;
    }
    if (!isNotReferredByCompany) {
      return;
    }

    // Guard to avoid infinite update loops: only clear if there is something to clear
    const hasLiveValues =
      companySelectionLive !== undefined || productSelectionLive !== undefined;
    const hasOptions =
      accountTypeOptions.length > 0 || accountTypeCompanyKey !== null;
    const existingData = (referedFromCompanyStepData ?? {}) as Record<
      string,
      unknown
    >;
    const needsFormClear = Boolean(
      existingData["referedFromCompany"] ||
        existingData["otherCompanyName"] ||
        existingData["productName"] ||
        existingData["accountType"] ||
        existingData["referenceId"] ||
        existingData["noReferenceId"]
    );

    if (!hasLiveValues && !hasOptions && !needsFormClear) {
      return;
    }

    if (companySelectionLive !== undefined) setCompanySelectionLive(undefined);
    if (productSelectionLive !== undefined) setProductSelectionLive(undefined);
    if (accountTypeOptions.length > 0) setAccountTypeOptions([]);
    if (accountTypeCompanyKey !== null) setAccountTypeCompanyKey(null);
    autoSelectedAccountTypeRef.current = null;
    lastCompanySelectionRef.current = undefined;

    if (needsFormClear) {
      const nextData: Record<string, unknown> = {
        ...existingData,
        notReferredByCompany: true,
        referedFromCompany: "",
        otherCompanyName: "",
        productName: "",
        accountType: "",
        referenceId: "",
        noReferenceId: false,
      };
      setFormData("onboarding-step-1", nextData);

      // Only remount form when we actually changed persisted data (validation needs reset)
      remountStep1Form();
    }
  }, [
    currentStep,
    isNotReferredByCompany,
    companySelectionLive,
    productSelectionLive,
    accountTypeOptions.length,
    accountTypeCompanyKey,
    referedFromCompanyStepData,
    setFormData,
    remountStep1Form,
  ]);

  // Clear product selection from form data when company changes (user-driven changes only)
  useEffect(() => {
    if (currentStep !== 1) {
      return;
    }

    const currentCompany = referedFromCompanyValue;
    const lastCompany = lastCompanySelectionRef.current;

    // Only clear if:
    // 1. We have both current and last company (not initial load)
    // 2. They are different (company actually changed)
    // 3. lastCompany is not undefined (ensures at least one selection was made)
    if (
      currentCompany &&
      lastCompany &&
      lastCompany !== undefined &&
      currentCompany !== lastCompany
    ) {
      // Company has changed - clear product-related fields from form data
      // Only aggressively clear when we are not simply restoring from a back-navigation
      const comingFromBackNav = !productRestoreAttemptedRef.current;
      const existingData = (referedFromCompanyStepData || {}) as Record<
        string,
        unknown
      >;
      const nextData: Record<string, unknown> = comingFromBackNav
        ? existingData
        : {
            ...existingData,
            productName: "",
            referenceId: "",
            accountType: "",
            noReferenceId: false,
          };
      setFormData("onboarding-step-1", nextData);
      // Also clear options/state so UI reconfigures for the new company
      if (accountTypeOptions.length > 0) {
        setAccountTypeOptions([]);
      }
      if (accountTypeCompanyKey !== null) {
        setAccountTypeCompanyKey(null);
      }
      autoSelectedAccountTypeRef.current = null;
      setProductSelectionLive(undefined);
      // Reset validation/touched state for dependent fields
      remountStep1Form();
    }
  }, [
    currentStep,
    referedFromCompanyValue,
    referedFromCompanyStepData,
    setFormData,
  ]);

  useEffect(() => {
    if (currentStep !== 1) {
      return;
    }

    const selectedValue = referedFromCompanyValue;
    const notReferred = isNotReferredByCompany;

    if (notReferred || !selectedValue) {
      // Ensure account type visibility/options are reset when not referred
      if (accountTypeOptions.length > 0) {
        setAccountTypeOptions([]);
      }
      if (accountTypeCompanyKey !== null) {
        setAccountTypeCompanyKey(null);
      }
      autoSelectedAccountTypeRef.current = null;
      return;
    }

    // Check if user selected "Can't find my referring company"
    const isNonDigitalCompany = selectedValue === OTHER_COMPANY_OPTION.value;

    // For non-digital (OTHER_COMPANY) companies, always fetch all account types
    if (isNonDigitalCompany) {
      const companyName = selectedValue;
      const isSameCompany = accountTypeCompanyKey === companyName;

      if (!isSameCompany && accountTypeOptions.length > 0) {
        setAccountTypeOptions([]);
      }

      if (isSameCompany && accountTypeOptions.length > 0) {
        return;
      }

      let cancelled = false;

      const loadAllAccountTypes = async (): Promise<void> => {
        try {
          setLoading("account-types-load", true);
          const response = await LenderService.fetchAllAccountTypes({
            skipErrorCapture: false,
          });
          if (cancelled) {
            return;
          }
          if (response.success) {
            setAccountTypeOptions(
              Array.isArray(response.data) ? response.data : []
            );
          } else {
            setAccountTypeOptions([]);
          }
          setAccountTypeCompanyKey(companyName);
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to load all account types:", error);
            setAccountTypeOptions([]);
            setAccountTypeCompanyKey(companyName);
          }
        } finally {
          if (!cancelled) {
            setLoading("account-types-load", false);
          }
        }
      };

      void loadAllAccountTypes();

      return () => {
        cancelled = true;
      };
    }

    // Get current product value (same logic used elsewhere for consistency)
    const liveRefProduct = getString(liveStepValuesRef.current, "productName");
    const persistedProduct = getString(
      referedFromCompanyStepData,
      "productName"
    );
    const currentProductValue =
      productSelectionLive || liveRefProduct || persistedProduct;
    const trimmedProduct =
      typeof currentProductValue === "string" ? currentProductValue.trim() : "";

    // For regular companies, only fetch/show account type when addToDebtList is Y
    // Check parent-level flag first
    const parentAddToDebtListY =
      isAffirmative(selectedCompany?.addToDebtList) ||
      isAffirmative(
        (selectedCompany?.raw as Record<string, unknown> | undefined)
          ?.addToDebtList
      );

    // Check child-level flag if a product is selected (override parent flag)
    let childAddToDebtListY: boolean | undefined;
    if (
      trimmedProduct &&
      selectedCompany &&
      Array.isArray(
        (selectedCompany as unknown as { childrenRaw?: unknown[] }).childrenRaw
      )
    ) {
      const childrenRaw =
        (
          selectedCompany as unknown as {
            childrenRaw?: Array<Record<string, unknown>>;
          }
        ).childrenRaw ?? [];
      const pNorm = trimmedProduct.toLowerCase();
      const childRawMatch = childrenRaw.find((child) => {
        const childLender =
          typeof (child as { lender?: string }).lender === "string"
            ? (child as { lender?: string }).lender!.trim().toLowerCase()
            : "";
        const childDesc =
          typeof (child as { description?: string }).description === "string"
            ? (child as { description?: string })
                .description!.trim()
                .toLowerCase()
            : "";
        return childLender === pNorm || childDesc === pNorm;
      });
      if (childRawMatch) {
        childAddToDebtListY = isAffirmative(
          (childRawMatch as { addToDebtList?: unknown }).addToDebtList
        );
      }
    }

    // Use child-level flag if defined, otherwise use parent-level flag
    const effectiveAddToDebtListY =
      childAddToDebtListY !== undefined
        ? childAddToDebtListY
        : parentAddToDebtListY;

    if (!effectiveAddToDebtListY) {
      if (accountTypeOptions.length > 0) {
        setAccountTypeOptions([]);
      }
      if (accountTypeCompanyKey !== null) {
        setAccountTypeCompanyKey(null);
      }
      return;
    }

    const companyName = selectedCompany?.parentCompany?.trim() || selectedValue;
    if (!companyName) {
      return;
    }

    // Use product as lookup key when selected, otherwise use company name
    // This matches the behavior in CompanyReferralEditDialog (profile page)
    const accountTypeLookupKey = trimmedProduct || companyName;

    const isSameKey = accountTypeCompanyKey === accountTypeLookupKey;
    if (!isSameKey && accountTypeOptions.length > 0) {
      setAccountTypeOptions([]);
    }

    if (isSameKey && accountTypeOptions.length > 0) {
      return;
    }

    let cancelled = false;

    const loadAccountTypes = async (): Promise<void> => {
      try {
        setLoading("account-types-load", true);
        const response = await LenderService.fetchAccountTypesByCompany(
          accountTypeLookupKey,
          { skipErrorCapture: false }
        );
        if (cancelled) {
          return;
        }
        if (response.success) {
          setAccountTypeOptions(
            Array.isArray(response.data) ? response.data : []
          );
        } else {
          setAccountTypeOptions([]);
        }
        setAccountTypeCompanyKey(accountTypeLookupKey);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load account types:", error);
          setAccountTypeOptions([]);
          setAccountTypeCompanyKey(accountTypeLookupKey);
        }
      } finally {
        if (!cancelled) {
          setLoading("account-types-load", false);
        }
      }
    };

    void loadAccountTypes();

    return () => {
      cancelled = true;
    };
  }, [
    currentStep,
    referedFromCompanyValue,
    isNotReferredByCompany,
    selectedCompany,
    accountTypeCompanyKey,
    accountTypeOptions.length,
    setLoading,
    productSelectionLive,
    referedFromCompanyStepData,
  ]);

  // Auto-select account type when exactly one unique option is available
  useEffect(() => {
    if (currentStep !== 1) {
      autoSelectedAccountTypeRef.current = null;
      return;
    }

    const notReferred = isNotReferredByCompany;
    const selectedValue = referedFromCompanyValue;
    if (notReferred || !selectedValue) {
      autoSelectedAccountTypeRef.current = null;
      return;
    }

    const uniqueCodes = Array.from(
      new Set(
        accountTypeOptions
          .map((item) =>
            typeof item.code === "string" ? item.code.trim() : ""
          )
          .filter((code) => code.length > 0)
      )
    );

    if (uniqueCodes.length !== 1) {
      autoSelectedAccountTypeRef.current = null;
      return;
    }

    const autoValue = uniqueCodes[0];
    if (!autoValue) {
      autoSelectedAccountTypeRef.current = null;
      return;
    }

    const existingAccountType =
      getString(referedFromCompanyStepData, "accountType")?.trim() ?? "";
    if (existingAccountType === autoValue) {
      autoSelectedAccountTypeRef.current = autoValue;
      return;
    }

    if (autoSelectedAccountTypeRef.current === autoValue) {
      return;
    }

    autoSelectedAccountTypeRef.current = autoValue;

    // Preserve all live values including product selection
    const currentLiveValues =
      currentStep === 1 ? (liveStepValuesRef.current ?? {}) : {};
    const mergedStepData: Record<string, unknown> = {
      ...(referedFromCompanyStepData ?? {}),
      ...currentLiveValues,
      referedFromCompany: referedFromCompanyValue,
      accountType: autoValue,
    };

    liveStepValuesRef.current = {
      ...liveStepValuesRef.current,
      referedFromCompany: referedFromCompanyValue,
      accountType: autoValue,
    };

    setFormData("onboarding-step-1", mergedStepData);

    // Don't bump form instance key if we have a product selection in progress
    // This prevents losing the product value during form reset
    const hasProductInProgress = Boolean(
      productSelectionLive || currentLiveValues.productName
    );
    if (!hasProductInProgress) {
      setTimeout(() => {
        bumpFormInstanceKey(1);
      }, 0);
    }
  }, [
    accountTypeOptions,
    bumpFormInstanceKey,
    currentStep,
    isNotReferredByCompany,
    referedFromCompanyStepData,
    referedFromCompanyValue,
    setFormData,
    productSelectionLive,
  ]);

  // Measure header and footer heights (mobile) to prevent content being cut
  useEffect(() => {
    const initializeComponent = async () => {
      // we need to check if address verification is needed or not.
      let target = user ? await processAddressVerification(user.userId) : "";
      if (target.includes("verify-address")) {
        navigate(target, { replace: true });
      }
    };

    initializeComponent();

    const updateHeights = () => {
      const newHeaderHeight = headerRef.current?.offsetHeight ?? 0;
      const newFooterHeight = footerRef.current?.offsetHeight ?? 0;

      // Only update if we have valid measurements (non-zero)
      if (newHeaderHeight > 0) {
        setHeaderHeight(newHeaderHeight);
      }
      if (newFooterHeight > 0) {
        setFooterHeight(newFooterHeight);
      }
    };

    // Use requestAnimationFrame to ensure DOM layout is complete before measuring
    // This prevents the issue where measurements are taken before elements are fully rendered
    const initialMeasurement = () => {
      requestAnimationFrame(() => {
        updateHeights();
        // Double-check after a short delay to catch any late-rendering elements
        setTimeout(updateHeights, 100);
      });
    };

    initialMeasurement();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateHeights);
      if (headerRef.current) resizeObserver.observe(headerRef.current);
      if (footerRef.current) resizeObserver.observe(footerRef.current);
    }

    window.addEventListener("resize", updateHeights);
    return () => {
      window.removeEventListener("resize", updateHeights);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Scroll to top whenever the user moves to a new step/question
  useEffect(() => {
    const scrollToTop = (el: HTMLElement | null) => {
      if (!el) return;
      if (typeof el.scrollTo === "function") {
        el.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        el.scrollTop = 0;
      }
    };

    scrollToTop(mobileContentRef.current);
    scrollToTop(desktopContentRef.current);

    if (
      typeof window !== "undefined" &&
      typeof window.scrollTo === "function"
    ) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep, screenRenderKey]);

  // Fallback: ensure Terms & Conditions are accepted after auth
  useEffect(() => {
    if (!user?.userId) return;
    let cancelled = false;
    (async () => {
      try {
        const customer = await OnboardingService.getCustomerByUserId(
          user.userId,
          { skipErrorCapture: true }
        );
        const tncAccepted = Boolean(
          customer.data &&
            (customer.data.tncFlag === "Y" || customer.data.tncFlag === "y")
        );
        const hasCustomerId = Boolean(
          customer.data && (customer.data.customerId || customer.data.id)
        );
        if (!cancelled && (!hasCustomerId || !tncAccepted)) {
          await TermsService.acceptTermsForUser(
            {
              userId: user.userId,
              displayName: user.displayName,
              emailAddress: user.emailAddress,
            },
            { skipErrorCapture: true }
          );
        }
      } catch {
        // non-blocking fallback; proceed with onboarding regardless
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.userId, user?.displayName, user?.emailAddress]);

  // Handle dynamic schema updates when vulnerability answer changes
  useEffect(() => {
    const vulnerabilityStepKey = "onboarding-step-7";
    const vulnerabilityData = allFormData[vulnerabilityStepKey] as
      | Record<string, unknown>
      | undefined;
    const vulnerabilityAnswer =
      vulnerabilityData?.hasVulnerabilityCircumstances as string;

    // If user is currently on a vulnerability follow-up step but answered "no",
    // redirect them back to the vulnerability question or completion
    if (currentStep >= 8 && vulnerabilityAnswer === "no") {
      setIsOnboardingComplete(true);
    }
  }, [allFormData, currentStep]);

  // When entering/leaving vulnerability step, sync selection from stored form data
  useEffect(() => {
    if (currentStep === 7) {
      const stepData =
        (allFormData["onboarding-step-7"] as
          | Record<string, unknown>
          | undefined) || {};
      const initialVal = stepData?.hasVulnerabilityCircumstances as
        | string
        | undefined;
      setVulnerabilitySelection(initialVal);
    } else {
      setVulnerabilitySelection(undefined);
    }
  }, [currentStep, allFormData]);

  // Form handlers
  const handleFormSubmit = async (data: unknown) => {
    const formData = data as Record<string, unknown>;
    const formId = `onboarding-step-${currentStep}`;

    // Remove previous auto-fill behavior: do not auto-populate referenceId with email
    // If needed, backend clearing logic will ensure stale values are removed

    // Save current step data to FormSlice (merge with any existing step data so hidden/auto fields persist)
    const previousStepData =
      (allFormData[formId] as Record<string, unknown> | undefined) ?? {};
    const mergedStepData: Record<string, unknown> = {
      ...previousStepData,
      ...formData,
    };

    // Step 6 (address) — backend expects postCode with no spaces (legacy parity)
    if (currentStep === 6) {
      const postcodeCandidate =
        typeof mergedStepData["postcode"] === "string"
          ? mergedStepData["postcode"]
          : typeof mergedStepData["postCode"] === "string"
            ? mergedStepData["postCode"]
            : "";
      const normalizedPostCode =
        typeof postcodeCandidate === "string" &&
        postcodeCandidate.trim().length > 0
          ? postcodeCandidate.replace(/\s+/g, "").toUpperCase()
          : "";
      if (normalizedPostCode) {
        mergedStepData["postcode"] = normalizedPostCode;
        mergedStepData["postCode"] = normalizedPostCode;
      }
    }
    setFormData(formId, mergedStepData);

    // Get all accumulated form data including current step
    const allData = { ...allFormData, [formId]: mergedStepData };
    let recordIdToUse: number | undefined;
    let customerIdToUse: number | undefined;
    try {
      const customerDataStr = localStorage.getItem("customerData");
      const customerData = customerDataStr
        ? (JSON.parse(customerDataStr) as Record<string, any>)
        : null;
      // Prefer true record id for payload.id, and customerId for payload.customerId
      recordIdToUse = (customerData?.id ?? customerData?.customerId) as
        | number
        | undefined;
      customerIdToUse = (customerData?.customerId ?? customerData?.id) as
        | number
        | undefined;
      // Guards: avoid using values equal to userId
      if (typeof recordIdToUse === "number" && recordIdToUse === user?.userId) {
        recordIdToUse = undefined;
      }
      if (
        typeof customerIdToUse === "number" &&
        customerIdToUse === user?.userId
      ) {
        customerIdToUse = undefined;
      }
    } catch {
      // ignore parse errors and fall back to undefined
    }

    // Persist stage as soon as submit is invoked
    persistFieldStage(currentStep);

    // Special handling for vulnerability circumstances step (index 7)
    if (currentStep === 7) {
      setLoading("onboarding-submit", true);
      try {
        const vulnerabilityAnswer =
          formData?.hasVulnerabilityCircumstances as string;
        // Resolve a reliable customerId for API updates
        let effectiveCustomerId: number | null =
          (typeof customerIdToUse === "number" ? customerIdToUse : null) ??
          (await ensureFieldStageCustomerId());
        if (!effectiveCustomerId && typeof user?.userId === "number") {
          try {
            const resp = await OnboardingService.getCustomerByUserId(
              user.userId,
              { skipErrorCapture: true }
            );
            const cId = (resp?.data?.customerId ?? resp?.data?.id) as
              | number
              | undefined;
            effectiveCustomerId = typeof cId === "number" ? cId : null;
          } catch {
            // non-blocking
          }
        }

        // If user clicks primary while no option is selected (label shows "Skip & continue")
        if (!vulnerabilityAnswer) {
          try {
            // Save step-7 payload and clear step-9 categories in vulnerability store
            if (effectiveCustomerId) {
              await OnboardingService.updateVulnerability(
                {
                  userId: user?.userId,
                  "onboarding-step-7": formData,
                  "onboarding-step-9": { vulnerabilityCircumstancesDetails: [] },
                },
                effectiveCustomerId,
                { skipErrorCapture: false }
              );
            }
          } finally {
            // Legacy parity: fire Meta Pixel + CAPI event on complete onboarding
            void trackMetaEvent("CompleteRegistration", "CompleteRegistration");
            // Persist as completed/escaped stage beyond onboarding
            try {
              const ensured =
                effectiveCustomerId ?? (await ensureFieldStageCustomerId());
              if (ensured) {
                await Promise.race([
                  persistFieldStage(15),
                  new Promise<void>((resolve) => setTimeout(resolve, 5000)),
                ]);
              }
            } finally {
              navigate("/dashboard", { replace: true });
            }
          }
          return;
        }

        if (vulnerabilityAnswer === "no") {
          try {
            // Save step-7 payload and clear step-9 categories in vulnerability store
            if (effectiveCustomerId) {
              await OnboardingService.updateVulnerability(
                {
                  userId: user?.userId,
                  "onboarding-step-7": formData,
                  "onboarding-step-9": { vulnerabilityCircumstancesDetails: [] },
                },
                effectiveCustomerId,
                { skipErrorCapture: false }
              );
              // Update customer gating flag and clear related fields
              await ProfileService.patchCustomerData(effectiveCustomerId, {
                field2TxtFld: "N",
                field3TxtFld: "",
                field4TxtFld: "",
              } as any);
            }
          } finally {
            // Legacy parity: fire Meta Pixel + CAPI event on complete onboarding
            void trackMetaEvent("CompleteRegistration", "CompleteRegistration");
            // For "No", mark stage as beyond onboarding and send to dashboard
            try {
              const ensured =
                effectiveCustomerId ?? (await ensureFieldStageCustomerId());
              if (ensured) {
                await Promise.race([
                  persistFieldStage(15),
                  new Promise<void>((resolve) => setTimeout(resolve, 5000)),
                ]);
              }
            } finally {
              navigate("/dashboard", { replace: true });
            }
          }
          return;
        }

        if (vulnerabilityAnswer === "yes") {
          // Set gating flag on the customer record before proceeding to follow-up steps
          try {
            if (effectiveCustomerId) {
              await ProfileService.patchCustomerData(effectiveCustomerId, {
                field2TxtFld: "Y",
              } as any);
            }
          } finally {
            // If "yes", continue to next step (first vulnerability-followup index 8)
            goToStep(8, {
              assumeTotalSteps:
                onboardingSchemas.length + vulnerabilityYesSchemas.length,
            });
            persistFieldStage(8);
          }
          return;
        }
      } finally {
        setLoading("onboarding-submit", false);
      }
    } else if (currentStep === 10) {
      // Legacy parity: fire Meta Pixel + CAPI event on complete onboarding
      void trackMetaEvent("CompleteRegistration", "CompleteRegistration");
      // This is the last vulnerability step, complete onboarding
      let responseCustomer = await OnboardingService.updateCustomer(
        allData,
        user?.userId as number,
        recordIdToUse as number,
        customerIdToUse as number,
        user?.emailAddress as string,
        { skipErrorCapture: false }
      );
      if (responseCustomer) {
        // Show brief success message
        // addAlert({
        //   severity: 'success',
        //   message: 'Progress saved',
        //   autoHide: true,
        //   duration: 1500
        // });
      }
      // Persist completion at last step — user cannot proceed until field stage is confirmed,
      // so wait for the full response (up to 60s failsafe before navigating anyway)
      try {
        await Promise.race([
          persistFieldStage(11),
          new Promise<void>((resolve) => setTimeout(resolve, 120000)),
        ]);
      } finally {
        navigate("/dashboard", { replace: true });
      }
      return;
    } else {
      // Determine desired next step up-front (handles step 0 branching)
      const selectedReferMethod =
        currentStep === 0 ? (formData?.referedFrom as string) || "" : "";
      const leadsToCompany =
        currentStep === 0 &&
        (selectedReferMethod === "Referred by a company" ||
          selectedReferMethod === "Debt charity or advice service");
      const desiredNextStep =
        currentStep === 0 ? (leadsToCompany ? 1 : 2) : currentStep + 1;
      const desiredNextStepAdjusted = Math.max(desiredNextStep, earliestStep);

      // If on step 0, always clear step 1 data before saving (user may have changed referral method)
      // This ensures stale company selections don't persist when user changes referral choice
      if (currentStep === 0) {
        const step1Data = allFormData["onboarding-step-1"];
        const step0Data = allData["onboarding-step-0"];
        const referMethodForReset = isRecord(step0Data)
          ? step0Data.referedFrom
          : undefined;
        if (shouldResetCompanyStepForReferral(referMethodForReset, step1Data)) {
          console.log(
            "🔄 User on step 0 with existing step 1 data - clearing to prevent stale data"
          );
          // Clear step 1 in allData before saving
          allData["onboarding-step-1"] = {};
          setFormData("onboarding-step-1", {});

          // Clear cached customer data to prevent stale field5TxtFld from being sent
          try {
            // Clear OnboardingService in-memory cache
            if (user?.userId) {
              OnboardingService.clearCachedCompanyField(user.userId);
            }

            // Clear localStorage cache
            const customerDataStr = localStorage.getItem("customerData");
            if (customerDataStr) {
              const customerDataObj = JSON.parse(customerDataStr) as Record<
                string,
                unknown
              >;
              if (customerDataObj.field5TxtFld) {
                customerDataObj.field5TxtFld = "";
                localStorage.setItem(
                  "customerData",
                  JSON.stringify(customerDataObj)
                );
                setCustomerData(customerDataObj);
                console.log("🧹 Cleared field5TxtFld from localStorage cache");
              }
            }
          } catch (e) {
            console.error("Failed to clear cached customer data", e);
          }

          // Also clear debts if they exist
          try {
            const ensuredCustomerId =
              fieldStageCustomerIdRef.current ??
              (await ensureFieldStageCustomerId());
            if (ensuredCustomerId) {
              const debts = await OnboardingService.getDebts(
                ensuredCustomerId,
                { skipErrorCapture: true }
              );
              if (Array.isArray(debts) && debts.length > 0) {
                await Promise.all(
                  debts.map((debt) => {
                    const debtId =
                      typeof debt?.id === "number" ? debt.id : undefined;
                    return debtId
                      ? OnboardingService.deleteDebts(
                          ensuredCustomerId,
                          debtId,
                          { skipErrorCapture: true }
                        )
                      : undefined;
                  })
                );
              }
            }
          } catch {
            // Debt cleanup failure is non-blocking
          }
        }
      }

      // Progressive API call - save progress after each step
      try {
        setLoading("onboarding-submit", true);

        // Check if user is authenticated and has id
        if (!user?.userId) {
          addAlert({
            severity: "error",
            message: "User not authenticated or user id not available",
            autoHide: true,
            duration: 3000,
          });
          throw new Error("User not authenticated or user id not available");
        }

        // If no customer exists yet, create a minimal one first
        // Ensure we have the authoritative ids from /customers/{userId}
        if (!recordIdToUse || !customerIdToUse) {
          try {
            const custResp = await OnboardingService.getCustomerByUserId(
              user.userId,
              { skipErrorCapture: false }
            );
            const data = custResp?.data as Record<string, any> | undefined;
            const fetchedId = (data?.id ?? data?.customerId) as
              | number
              | undefined;
            const fetchedCustomerId = (data?.customerId ?? data?.id) as
              | number
              | undefined;
            recordIdToUse =
              typeof fetchedId === "number" && fetchedId !== user.userId
                ? fetchedId
                : recordIdToUse;
            customerIdToUse =
              typeof fetchedCustomerId === "number" &&
              fetchedCustomerId !== user.userId
                ? fetchedCustomerId
                : customerIdToUse;
          } catch (fetchErr) {
            console.error("Failed to fetch customer before update", fetchErr);
          }
        }

        // If still no customer id, create a new customer (first-time user) via POST
        if (!recordIdToUse || !customerIdToUse) {
          try {
            const createResp = await OnboardingService.createCustomer(
              user.userId,
              {},
              { skipErrorCapture: false }
            );
            if (createResp?.success && createResp.data) {
              recordIdToUse = (createResp.data.id ??
                createResp.data.customerId) as number | undefined;
              customerIdToUse = (createResp.data.customerId ??
                createResp.data.id) as number | undefined;
              try {
                localStorage.setItem(
                  "customerData",
                  JSON.stringify(createResp.data)
                );
              } catch {}
            } else {
              // If POST didn't create a usable record, continue UX without saving
              goToStep(desiredNextStep);
              return;
            }
          } catch (createErr) {
            console.error("Failed to create customer record", createErr);
            // Continue UX without saving if creation fails
            goToStep(desiredNextStep);
            return;
          }
        }

        // Ensure field-stage refs are initialized once we have a valid customer id
        // (covers both getCustomerByUserId and createCustomer paths)
        if (
          !fieldStageInitializedRef.current &&
          typeof customerIdToUse === "number" &&
          customerIdToUse > 0
        ) {
          fieldStageCustomerIdRef.current = customerIdToUse;
          fieldStageInitializedRef.current = true;
        }

        // Final guard: ensure numeric ids before calling update
        if (
          typeof recordIdToUse !== "number" ||
          typeof customerIdToUse !== "number"
        ) {
          goToStep(desiredNextStep);
          return;
        }

        console.log("🔍 Submit Data:", {
          currentStep,
          desiredNextStepAdjusted,
          allData,
          step0: allData["onboarding-step-0"],
          step1: allData["onboarding-step-1"],
          userId: user.userId,
          recordIdToUse,
          customerIdToUse,
        });

        let response;

        if (currentStep === 1) {
          // Step 1 (company referral) – determine behavior based on addToDebtList and addToShareListFromProfile flags
          // These flags are INDEPENDENT - one doesn't imply the other
          const step1DataAll = allData["onboarding-step-1"] as
            | Record<string, unknown>
            | undefined;
          const companyKey =
            typeof step1DataAll?.referedFromCompany === "string"
              ? step1DataAll.referedFromCompany
              : "";
          const isOtherCompany = companyKey === OTHER_COMPANY_OPTION.value;
          const selectedProductName =
            typeof step1DataAll?.productName === "string"
              ? (step1DataAll.productName as string).trim()
              : "";

          // Find the company record from lenders
          const companyRec = findLenderByName(
            lenderRecordLookup,
            allLenders,
            companyKey
          );

          // Parent-level flags
          const parentAddToDebtListY =
            isAffirmative(companyRec?.addToDebtList) ||
            isAffirmative(
              (companyRec?.raw as Record<string, unknown> | undefined)
                ?.addToDebtList
            );
          const parentAddToShareListY =
            isAffirmative(companyRec?.addToShareListFromProfile) ||
            isAffirmative(
              (companyRec?.raw as Record<string, unknown> | undefined)
                ?.addToShareListFromProfile
            );

          // Child-level flags (if a product is selected)
          let childAddToDebtListY: boolean | undefined;
          let childAddToShareListY: boolean | undefined;
          if (
            selectedProductName &&
            companyRec &&
            Array.isArray((companyRec as any).childrenRaw)
          ) {
            const childrenRaw =
              (
                companyRec as unknown as {
                  childrenRaw?: Array<Record<string, unknown>>;
                }
              ).childrenRaw ?? [];
            const pNorm = normalizeString(selectedProductName);
            const childRawMatch = childrenRaw.find((child) => {
              const childLender = normalizeString(
                (child as { lender?: string }).lender
              );
              const childDesc = normalizeString(
                (child as { description?: string }).description
              );
              return childLender === pNorm || childDesc === pNorm;
            });
            if (childRawMatch) {
              childAddToDebtListY = isAffirmative(
                (childRawMatch as { addToDebtList?: unknown }).addToDebtList
              );
              childAddToShareListY = isAffirmative(
                (childRawMatch as { addToShareListFromProfile?: unknown })
                  .addToShareListFromProfile
              );
            }
          }

          // Effective flags: prefer child-level if defined, otherwise use parent-level
          // These are INDEPENDENT checks - one doesn't depend on the other
          const effectiveAddToDebtListY =
            childAddToDebtListY !== undefined
              ? childAddToDebtListY
              : parentAddToDebtListY;
          const effectiveAddToShareListY =
            childAddToShareListY !== undefined
              ? childAddToShareListY
              : parentAddToShareListY;

          // For OTHER_COMPANY, always create debt (legacy behavior for manual entries)
          const shouldCreateDebt = isOtherCompany || effectiveAddToDebtListY;
          // Share list is independent - only check the addToShareListFromProfile flag
          const shouldUpsertSharing = effectiveAddToShareListY;

          // Always update customer data first
          response = await OnboardingService.updateCustomer(
            allData,
            user.userId,
            recordIdToUse,
            customerIdToUse,
            user.emailAddress,
            { skipErrorCapture: false }
          );

          // If the user has explicitly said they were not referred by a company,
          // ensure any profile-managed debts and referral-based sharing entries are unlinked.
          const notReferred = step1DataAll?.notReferredByCompany === true;
          if (notReferred && customerIdToUse) {
            try {
              await OnboardingService.unlinkProfileManagedDebtAndSharing(
                customerIdToUse,
                { skipErrorCapture: true }
              );
            } catch {
              // Non-blocking: failing to unlink should not block saving referral details
            }
          }

          // Handle debt creation/update independently based on addToDebtList flag
          if (shouldCreateDebt && customerIdToUse) {
            try {
              await OnboardingService.updateCustomerAndDebt(
                allData,
                user.userId,
                recordIdToUse,
                customerIdToUse,
                user.emailAddress,
                {
                  skipErrorCapture: false,
                  // Skip sharing upsert inside updateCustomerAndDebt when we're handling it ourselves.
                  // This prevents creating duplicate sharing records when addToShareListFromProfile is enabled.
                  skipSharingUpsert: shouldUpsertSharing,
                }
              );
            } catch {
              // Non-blocking: debt creation should not block save
            }
          }

          // Handle sharing based on addToShareListFromProfile flag (parity with profile dialog)
          if (customerIdToUse) {
            try {
              // FIRST: Call GET to check if ANY sharing row with source: "customer_profile"
              // (or legacy "referred") exists for this customer.
              const existingResp = await DebtsService.getCustomerSharing(
                customerIdToUse,
                {
                  skipErrorCapture: true,
                }
              );
              const existingRows = Array.isArray(existingResp?.data)
                ? (existingResp.data as unknown[])
                : [];

              // IMPORTANT (legacy parity):
              // - Sharing rows that have already been shared (submitDate/lastSharedDate present) are immutable history.
              // - Therefore: only UPDATE (PUT) an existing profile-managed row if it has NEVER been shared.
              // - If it was already shared, treat it as "not updatable" and CREATE (POST) a new profile-managed row.
              const isNonEmptyString = (v: unknown): v is string =>
                typeof v === "string" && v.trim().length > 0;
              const isSharedRow = (row: Record<string, unknown>): boolean => {
                const submit = isNonEmptyString(row["submitDate"])
                  ? row["submitDate"].trim()
                  : "";
                const lastShared = isNonEmptyString(row["lastSharedDate"])
                  ? row["lastSharedDate"].trim()
                  : "";
                return Boolean(submit || lastShared);
              };
              const isProfileManagedRow = (
                row: Record<string, unknown>
              ): boolean => {
                const src = (row["source"] ?? "")
                  .toString()
                  .trim()
                  .toLowerCase();
                // IMPORTANT:
                // - Back end uses `isDeleted: 'Y'` to indicate logical deletion of a sharing row.
                // - There is also an `isDelete` flag which is used for a different concern and
                //   MUST NOT be treated as a deletion marker for sharing rows here.
                // - Only `isDeleted === 'Y'` should exclude a row from profile‑managed updates.
                const isDeleted = row["isDeleted"] === "Y";
                return (
                  !isDeleted &&
                  (src === "customer_profile" || src === "referred")
                );
              };
              const toTime = (v: unknown): number => {
                if (!isNonEmptyString(v)) return 0;
                const t = Date.parse(v);
                return Number.isFinite(t) ? t : 0;
              };
              const selectMostRecentUnsharedProfileManaged = (
                rows: unknown[]
              ): Record<string, unknown> | null => {
                const candidates = rows
                  .filter(
                    (r): r is Record<string, unknown> =>
                      typeof r === "object" && r !== null
                  )
                  .filter((r) => isProfileManagedRow(r) && !isSharedRow(r));
                if (candidates.length === 0) return null;
                return (
                  candidates.slice().sort((a, b) => {
                    const ta = Math.max(
                      toTime(a["lastUpdateDate"]),
                      toTime(a["creationDate"])
                    );
                    const tb = Math.max(
                      toTime(b["lastUpdateDate"]),
                      toTime(b["creationDate"])
                    );
                    return tb - ta;
                  })[0] ?? null
                );
              };

              // Find the most recent *unshared* profile-managed sharing record (regardless of lenderName).
              // If the only profile-managed records are already shared, we intentionally return null
              // so the upsert path will CREATE a new record instead of mutating history.
              const existingReferredRecord =
                selectMostRecentUnsharedProfileManaged(existingRows);

              if (shouldUpsertSharing && companyRec) {
                // Company has addToShareListFromProfile: Y - create or update the referred sharing record
                const selectedParentCompany = companyRec.parentCompany || "";
                const raw =
                  (
                    companyRec as unknown as {
                      raw?: Record<string, unknown>;
                    } | null
                  )?.raw || {};
                const rawParent = raw;

                // If a product (child company) is selected, prefer the child's digital config
                // (schemaName/subDomain) over the parent.
                const childRawForProduct = (() => {
                  const selectedProductName =
                    typeof step1DataAll?.productName === "string"
                      ? (step1DataAll.productName as string).trim()
                      : "";
                  if (!selectedProductName) return null;
                  const childrenRaw = (
                    companyRec as unknown as {
                      childrenRaw?: Array<Record<string, unknown>>;
                    }
                  ).childrenRaw ?? [];
                  const pNorm = normalizeString(selectedProductName);
                  const match = childrenRaw.find((child) => {
                    const childLender = normalizeString(
                      (child as { lender?: string }).lender
                    );
                    const childDesc = normalizeString(
                      (child as { description?: string }).description
                    );
                    return childLender === pNorm || childDesc === pNorm;
                  });
                  return match || null;
                })();

                const resolveString = (v: unknown): string | undefined => {
                  if (typeof v !== "string") return undefined;
                  const t = v.trim();
                  return t.length > 0 ? t : undefined;
                };

                const lenderName =
                  resolveString((childRawForProduct as any)?.lender) ??
                  resolveString((rawParent as any)?.lender) ??
                  resolveString(selectedParentCompany) ??
                  "";

                const schemaName =
                  resolveString((childRawForProduct as any)?.schemaName) ??
                  resolveString((rawParent as any)?.schemaName);
                const subDomain =
                  resolveString((childRawForProduct as any)?.subDomain) ??
                  resolveString((rawParent as any)?.subDomain);
                const explicitRef =
                  typeof step1DataAll?.referenceId === "string"
                    ? (step1DataAll.referenceId as string).trim()
                    : "";
                const accountNumber =
                  explicitRef || (user.emailAddress || "").trim();

                if (accountNumber) {
                  const sharingPayload: CustomerSharingUpsertRequest = {
                    customerId: Number(customerIdToUse),
                    lenderName,
                    accountNumber,
                    ...(schemaName ? { schemaName } : {}),
                    ...(subDomain ? { subDomain } : {}),
                    shareNow: shouldDefaultShareNow(lenderName) ? "Y" : "N",
                    isSharing: "Y",
                    source: "customer_profile",
                  };

                  const existingId = existingReferredRecord?.["id"];
                  if (
                    existingReferredRecord &&
                    typeof existingId === "number"
                  ) {
                    // UPDATE existing profile-managed sharing record with new company info
                    await DebtsService.updateCustomerSharing(
                      ({
                        ...(existingReferredRecord as Record<string, unknown>),
                        ...sharingPayload,
                        id: existingId,
                      } as CustomerSharingUpsertRequest & Record<string, unknown>),
                      { skipErrorCapture: true }
                    );
                  } else {
                    // No existing profile-managed record found - CREATE new sharing record
                    await DebtsService.createCustomerSharing(sharingPayload, {
                      skipErrorCapture: true,
                    });
                  }
                }
              } else if (existingReferredRecord) {
                // Company has addToShareListFromProfile: N (or no company selected)
                // BUT there's an existing referred sharing record - update it with source:
                // "unlink_customer_profile". This marks the record as no longer profile-managed.
                // NOTE: `existingReferredRecord` is intentionally *unshared* (no lastSharedDate/submitDate),
                // so we never mutate historical shared rows.
                const existingId = existingReferredRecord["id"];
                if (typeof existingId === "number") {
                  const rowSchemaName =
                    typeof existingReferredRecord["schemaName"] === "string"
                      ? existingReferredRecord["schemaName"]
                      : null;
                  const rowSubDomain =
                    typeof existingReferredRecord["subDomain"] === "string"
                      ? existingReferredRecord["subDomain"]
                      : null;
                  await DebtsService.updateCustomerSharing(
                    ({
                      ...(existingReferredRecord as Record<string, unknown>),
                      id: existingId,
                      customerId: Number(customerIdToUse),
                      lenderName:
                        typeof existingReferredRecord["lenderName"] === "string"
                          ? existingReferredRecord["lenderName"]
                          : "",
                      accountNumber:
                        typeof existingReferredRecord["accountNumber"] ===
                        "string"
                          ? existingReferredRecord["accountNumber"]
                          : "",
                      schemaName: rowSchemaName,
                      subDomain: rowSubDomain,
                      shareNow: toYN(existingReferredRecord["shareNow"]),
                      isSharing: toYN(existingReferredRecord["isSharing"]),
                      source: "unlink_customer_profile",
                    } as CustomerSharingUpsertRequest & Record<string, unknown>),
                    { skipErrorCapture: true }
                  );
                }
              }
            } catch {
              // Non-blocking: sharing upsert should not block save
            }
          }
        } else if (desiredNextStepAdjusted === 10) {
          response = await OnboardingService.updateVulnerability(
            { ...allData, userId: user.userId },
            customerIdToUse,
            { skipErrorCapture: false }
          );
        } else if (currentStep === 6) {
          // Step 6 (address) — call Google API FIRST to enrich address, then PUT
          const buildingNumberVal =
            typeof mergedStepData["buildingNumber"] === "string"
              ? mergedStepData["buildingNumber"].trim()
              : "";
          const postcodeRaw =
            typeof mergedStepData["postcode"] === "string"
              ? mergedStepData["postcode"].trim()
              : typeof mergedStepData["postCode"] === "string"
                ? (mergedStepData["postCode"] as string).trim()
                : "";
          // Normalize postcode for backend (no spaces, uppercase)
          const postCodeForBackend = postcodeRaw
            ? postcodeRaw.replace(/\s+/g, "").toUpperCase()
            : "";

          console.log("📍 Step 6: Starting address enrichment", {
            postcode: postCodeForBackend,
            buildingNumber: buildingNumberVal,
          });

          // Call Google API with 3-minute timeout (180000ms)
          // This happens BEFORE the PUT request
          let enrichedAddressData: EnrichedAddressData;
          try {
            const googleResult = await getAddressFromGooglePlacesWithTimeout(
              postCodeForBackend,
              buildingNumberVal,
              180000 // 3 minutes timeout
            );

            console.log("📍 Step 6: Google API result", {
              success: !!googleResult,
              googleResult,
            });

            // Build enriched address data from Google result (or just postcode/building if null)
            enrichedAddressData = buildEnrichedAddressData(
              postCodeForBackend,
              buildingNumberVal,
              googleResult
            );

            console.log("📍 Step 6: Enriched address data", enrichedAddressData);
          } catch (googleError) {
            // Google API failed - proceed with just postcode and building number
            console.warn("📍 Step 6: Google API failed, using minimal address data", googleError);
            enrichedAddressData = buildEnrichedAddressData(
              postCodeForBackend,
              buildingNumberVal,
              null
            );
          }

          // Merge enriched address data into step 6 form data
          // Note: 'city' is NOT supported by backend CustomerModel - postTown is used instead
          const enrichedStep6Data = {
            ...(allData["onboarding-step-6"] as Record<string, unknown> || {}),
            postcode: enrichedAddressData.postCode,
            postCode: enrichedAddressData.postCode,
            buildingNumber: enrichedAddressData.buildingNumber || buildingNumberVal,
            streetName: enrichedAddressData.streetName,
            postTown: enrichedAddressData.postTown,
            state: enrichedAddressData.state,
            country: enrichedAddressData.country,
            buildingName: enrichedAddressData.buildingName,
          };

          // Update allData with enriched step 6 data
          const enrichedAllData = {
            ...allData,
            "onboarding-step-6": enrichedStep6Data,
          };

          console.log("📍 Step 6: Calling PUT with enriched data", enrichedStep6Data);

          // Now call the PUT with enriched address data
          response = await OnboardingService.updateCustomer(
            enrichedAllData,
            user.userId,
            recordIdToUse,
            customerIdToUse,
            user.emailAddress,
            { skipErrorCapture: false }
          );
        } else {
          response = await OnboardingService.updateCustomer(
            allData,
            user.userId,
            recordIdToUse,
            customerIdToUse,
            user.emailAddress,
            { skipErrorCapture: false }
          );
        }

        console.log("✅ Update Response:", response);

        // Call API to save progress

        if (response) {
          // no need to show alert here
          // addAlert({
          //   severity: 'success',
          //   message: 'Progress saved',
          //   autoHide: true,
          //   duration: 1500
          // });
        }

        // After successful save, move to the desired next step (handles step 0 branching)
        const selectedReferMethodPost =
          currentStep === 0 ? (formData?.referedFrom as string) || "" : "";
        const leadsToCompanyPost =
          currentStep === 0 &&
          (selectedReferMethodPost === "Referred by a company" ||
            selectedReferMethodPost === "Debt charity or advice service");
        const desiredNextStepPost =
          currentStep === 0 ? (leadsToCompanyPost ? 1 : 2) : currentStep + 1;
        const desiredNextStepPostAdjusted = Math.max(
          desiredNextStepPost,
          earliestStep
        );
        // Persist the next stage just before navigating
        persistFieldStage(desiredNextStepPostAdjusted);
        goToStep(desiredNextStepPostAdjusted);
      } catch (error) {
        console.error(error);
        const parsed = parseUnknownError(error);
        setErrorDialogTitle(parsed.title || "Something went wrong");
        setErrorDialogMessage(parsed.message || "Please try again.");
        setErrorDialogOpen(true);
        // Stay on current step so user can fix validation errors
        return;
      } finally {
        setLoading("onboarding-submit", false);
      }
    }
  };

  // Live values change handler to update vulnerability selection reactively
  const handleValuesChange = (vals: unknown) => {
    const record = vals as Record<string, unknown>;
    liveStepValuesRef.current = record;

    // Defer state updates to avoid "Can't update component while rendering" error
    // This ensures state updates happen after the current render cycle completes
    queueMicrotask(() => {
      // Note: Step 6 address enrichment is now done synchronously on submit (not debounced here)
      // Google API is called in handleFormSubmit before the PUT request

      if (currentStep === 7) {
        const sel = record?.hasVulnerabilityCircumstances as string | undefined;
        setVulnerabilitySelection(sel);
      }
      if (currentStep === 1) {
        const companySel = record?.referedFromCompany as string | undefined;
        const notRef = record?.notReferredByCompany as boolean | undefined;
        const noReferenceId = record?.noReferenceId as boolean | undefined;
        const productSel = record?.productName as string | undefined;

        // Detect if company has changed
        const companyChanged =
          typeof companySel === "string" &&
          companySel.trim().length > 0 &&
          lastCompanySelectionRef.current !== undefined &&
          lastCompanySelectionRef.current !== companySel;

        // Only update state if we have actual values
        // Skip updates for undefined (field not in record) OR empty strings (would clear fields)
        // UNLESS the company has changed (then we should clear product)
        const hasCompanyValue =
          companySel !== undefined &&
          (typeof companySel !== "string" || companySel.trim().length > 0);
        const hasProductValue =
          productSel !== undefined &&
          (typeof productSel !== "string" || productSel.trim().length > 0);

        // If user checks "Not Referred By a Company", clear dependent state immediately
        if (notRef === true) {
          const existingStep1 = (referedFromCompanyStepData ?? {}) as Record<
            string,
            unknown
          >;
          const alreadyCleared =
            existingStep1?.notReferredByCompany === true &&
            !getString(existingStep1, "referedFromCompany") &&
            !getString(existingStep1, "otherCompanyName") &&
            !getString(existingStep1, "productName") &&
            !getString(existingStep1, "accountType") &&
            !getString(existingStep1, "referenceId") &&
            existingStep1?.noReferenceId !== true;

          // Avoid re-triggering updates if state is already cleared
          if (!alreadyCleared) {
            setFormData("onboarding-step-1", {
              ...existingStep1,
              notReferredByCompany: true,
              referedFromCompany: "",
              otherCompanyName: "",
              productName: "",
              accountType: "",
              referenceId: "",
              noReferenceId: false,
            });
          }

          if (companySelectionLive !== undefined)
            setCompanySelectionLive(undefined);
          lastCompanySelectionRef.current = undefined;
          if (productSelectionLive !== undefined)
            setProductSelectionLive(undefined);
          if (accountTypeOptions.length > 0) setAccountTypeOptions([]);
          if (accountTypeCompanyKey !== null) setAccountTypeCompanyKey(null);
          autoSelectedAccountTypeRef.current = null;
        }

        // When toggling the "I don't have any Reference ID" checkbox back to false,
        // attempt to restore existing backend value if present; otherwise leave blank.
        if (currentStep === 1) {
          const wasNoReference = lastNoReferenceIdRef.current;
          if (wasNoReference === true && noReferenceId === false) {
            (async () => {
              try {
                const ensuredCustomerId =
                  fieldStageCustomerIdRef.current ??
                  (await ensureFieldStageCustomerId());
                if (ensuredCustomerId) {
                  const debts = await OnboardingService.getDebts(
                    ensuredCustomerId,
                    { skipErrorCapture: true }
                  );
                  const firstDebt =
                    Array.isArray(debts) && debts.length > 0 ? debts[0] : null;
                  const existingAccountNumber =
                    typeof firstDebt?.accountNumber === "string"
                      ? firstDebt.accountNumber
                      : "";
                  const currentStep1 = (referedFromCompanyStepData ??
                    {}) as Record<string, unknown>;
                  setFormData("onboarding-step-1", {
                    ...currentStep1,
                    noReferenceId: false,
                    referenceId: existingAccountNumber || "",
                  });
                }
              } catch {
                // non-blocking
              }
            })();
          }
          lastNoReferenceIdRef.current = noReferenceId;
        }

        if (hasCompanyValue) {
          const firstSelectionAfterReset =
            lastCompanySelectionRef.current === undefined;
          if (companySel !== companySelectionLive) {
            setCompanySelectionLive(companySel);
          }
          lastCompanySelectionRef.current = companySel;

          if (firstSelectionAfterReset) {
            const existingStep1 = (referedFromCompanyStepData ?? {}) as Record<
              string,
              unknown
            >;
            const notRefNow =
              typeof notRef === "boolean" ? notRef : isNotReferredByCompany;

            if (notRefNow) {
              // Coming from Not Referred → clear dependents
              const nextStep1: Record<string, unknown> = {
                ...existingStep1,
                notReferredByCompany: false,
                referedFromCompany:
                  typeof companySel === "string" ? companySel : "",
                productName: "",
                accountType: "",
                referenceId: "",
                noReferenceId: false,
              };
              setFormData("onboarding-step-1", nextStep1);
              if (accountTypeOptions.length > 0) setAccountTypeOptions([]);
              if (accountTypeCompanyKey !== null)
                setAccountTypeCompanyKey(null);
              autoSelectedAccountTypeRef.current = null;
              setProductSelectionLive(undefined);
              remountStep1Form();
            } else {
              // Normal back-navigation case: keep any existing product/accountType/reference
              const safeData: Record<string, unknown> = {
                ...existingStep1,
                notReferredByCompany: false,
                referedFromCompany:
                  typeof companySel === "string" ? companySel : "",
              };
              setFormData("onboarding-step-1", safeData);
            }
          }
        }

        // If company changed, clear the product selection regardless of productSel value
        if (companyChanged) {
          setProductSelectionLive(undefined);
          // Clear dependent values and options immediately and reset validation state
          const existingStep1 = (referedFromCompanyStepData ?? {}) as Record<
            string,
            unknown
          >;
          const nextStep1: Record<string, unknown> = {
            ...existingStep1,
            notReferredByCompany: false,
            referedFromCompany:
              typeof companySel === "string" ? companySel : "",
            productName: "",
            accountType: "",
            referenceId: "",
            noReferenceId: false,
          };
          setFormData("onboarding-step-1", nextStep1);
          if (accountTypeOptions.length > 0) {
            setAccountTypeOptions([]);
          }
          if (accountTypeCompanyKey !== null) {
            setAccountTypeCompanyKey(null);
          }
          autoSelectedAccountTypeRef.current = null;
          // Align live values with cleared state to avoid flicker
          liveStepValuesRef.current = {
            ...liveStepValuesRef.current,
            productName: "",
            accountType: "",
            referenceId: "",
            noReferenceId: false,
          };
          // Force a remount so validation/touched reset for dependent fields
          remountStep1Form();
        } else if (hasProductValue && productSel !== productSelectionLive) {
          setProductSelectionLive(productSel);
        }

        // Only update when explicitly provided and actually different
        if (typeof notRef === "boolean" && notRef !== notReferredByCompanyLive) {
          setNotReferredByCompanyLive(notRef);
        }
      }
    });
  };

  const handleBack = () => {
    if (currentStep > 0) {
      // Validate and restore phone number if leaving step 5 (contact details)
      if (currentStep === 5) {
        validateAndRestorePhoneNumber();
      }

      let previousStep = currentStep - 1;

      if (currentStep >= 8 && currentStep <= 10) {
        const currentFormData = { ...allFormData } as Record<string, unknown>;
        const shouldIncludeVulnerability =
          computeShouldIncludeFromData(currentFormData);
        if (!shouldIncludeVulnerability) {
          previousStep = 7;
        }
      }

      if (currentStep === 2) {
        const referMethodData = allFormData["onboarding-step-0"];
        const step1Data = allFormData["onboarding-step-1"];
        const referMethod = isRecord(referMethodData)
          ? referMethodData.referedFrom
          : undefined;
        if (shouldResetCompanyStepForReferral(referMethod, step1Data)) {
          void clearCompanyReferralStep({
            currentForm: allFormData as unknown as Record<string, unknown>,
            customerIdHint: fieldStageCustomerIdRef.current,
            userId: user?.userId ?? null,
            userEmail: user?.emailAddress,
            setFormData,
            setSchemaOverrides,
            setCompanySelectionLive,
            setProductSelectionLive,
            setNotReferredByCompanyLive,
            setAccountTypeOptions,
            setAccountTypeCompanyKey,
            accountTypeRef: autoSelectedAccountTypeRef,
            ensureFieldStageCustomerId,
            getCustomerSnapshot: () => customerData,
          });
        }
      }

      if (currentStep === 2) {
        const referMethodData = allFormData["onboarding-step-0"] as
          | Record<string, unknown>
          | undefined;
        const selectedReferMethod =
          (referMethodData?.referedFrom as string) || "";
        const leadsToCompany =
          selectedReferMethod === "Referred by a company" ||
          selectedReferMethod === "Debt charity or advice service";
        if (!leadsToCompany) {
          previousStep = 0;
        }
      }

      persistFieldStage(previousStep);
      goToStep(previousStep);
    } else {
      // TODO: Navigate to previous page or exit onboarding
    }
  };

  const handleSkip = () => {
    // Special handling for vulnerability circumstances step (index 7)
    if (currentStep === 7) {
      // Legacy parity: fire Meta Pixel + CAPI event on complete onboarding
      void trackMetaEvent("CompleteRegistration", "CompleteRegistration");
      // For skip action on vulnerability step, navigate directly to dashboard
      // Persist to a high stage to indicate onboarding is effectively complete
      (async () => {
        setLoading("onboarding-skip", true);
        try {
          const ensured = await ensureFieldStageCustomerId();
          if (ensured) {
            await Promise.race([
              persistFieldStage(15),
              new Promise<void>((resolve) => setTimeout(resolve, 5000)),
            ]);
          }
        } finally {
          setLoading("onboarding-skip", false);
          navigate("/dashboard", { replace: true });
        }
      })();
      return;
    }

    // Advance to next step without saving current step data
    const mergedData = { ...allFormData } as Record<string, unknown>;
    const nextStep = getNextStepIndex(currentStep, mergedData);
    persistFieldStage(nextStep);

    // Check if this would be the last step
    if (nextStep >= totalSteps - 1) {
      setIsOnboardingComplete(true);
    } else {
      goToStep(nextStep);
    }
  };

  const { openFaqForSchema } = useHelp();
  const handleHelpOpen = () => openFaqForSchema(currentSchema);

  const handleLogoutMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLogoutMenuAnchor(event.currentTarget);
  };

  const handleLogoutMenuClose = () => {
    setLogoutMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleLogoutMenuClose();
    handleMobileMenuClose();
    try {
      await logout({ source: "manual" }); // calls AuthService.logout and clears tokens
    } finally {
      navigate("/auth", { replace: true });
    }
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleCompletionContinue = () => {
    // Navigate to dashboard or main app
    // TODO: Navigate to dashboard
    // navigate('/dashboard');
  };

  // Extract user name for completion screen with strict priority: debtor1FirstName -> onboarding firstName -> displayName
  const getUserName = (): string => {
    // 1) Stored customer data: debtor1FirstName
    try {
      const raw = localStorage.getItem("customerData");
      if (raw) {
        const obj = JSON.parse(raw) as Record<string, unknown> | null;
        const debtor1FirstName =
          (obj?.debtor1FirstName as string | undefined) ||
          (obj?.firstName as string | undefined) ||
          "";
        if (
          typeof debtor1FirstName === "string" &&
          debtor1FirstName.trim().length > 0
        ) {
          return debtor1FirstName.trim();
        }
      }
    } catch {
      // ignore
    }

    // 2) Personal details collected in onboarding flow
    const personalDetailsKey = "onboarding-step-3"; // Personal details is now index 3
    const personalDetails = allFormData[personalDetailsKey] as
      | Record<string, unknown>
      | undefined;
    const firstName = personalDetails?.firstName as string | undefined;
    const lastName = personalDetails?.lastName as string | undefined;

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    }

    // 3) Fallback to authenticated user's displayName only (no email, no userName)
    const authPreferred = (
      (typeof user?.displayName === "string" && user.displayName) ||
      ""
    ).trim();
    if (authPreferred.length > 0) {
      return authPreferred;
    }

    return "User";
  };

  const shouldResetCompanyStepForReferral = (
    referMethod: unknown,
    stepOneData: unknown
  ): boolean => {
    if (typeof referMethod === "string") {
      const trimmed = referMethod.trim();
      if (
        trimmed === "Referred by a company" ||
        trimmed === "Debt charity or advice service"
      ) {
        return false;
      }
    } else if (referMethod !== undefined && referMethod !== null) {
      // Non-string values are treated as non-company referrals (and may indicate stale form payloads)
    } else {
      // Undefined/null refer method implies the user has not selected a company-based referral
    }

    if (!isRecord(stepOneData)) {
      return false;
    }

    const record = stepOneData as Record<string, unknown>;
    const relevantKeys = [
      "referedFromCompany",
      "otherCompanyName",
      "productName",
      "accountType",
      "referenceId",
      "notReferredByCompany",
      "noReferenceId",
    ] as const;

    return relevantKeys.some((key) => {
      const value = record[key as keyof typeof record];
      if (value === undefined || value === null) {
        return false;
      }
      if (typeof value === "string") {
        return value.trim().length > 0;
      }
      if (typeof value === "boolean" || typeof value === "number") {
        return true;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return String(value).trim().length > 0;
    });
  };

  // Helper function to validate and restore phone number from backend or clear it
  const validateAndRestorePhoneNumber = useCallback((): void => {
    const contactStepKey = "onboarding-step-5";
    const contactStepData =
      (allFormData[contactStepKey] as Record<string, unknown> | undefined) ||
      {};
    const currentPhoneNumber = contactStepData.phoneNumber;

    // Phone number validation pattern: only numbers, spaces, and + sign
    const phonePattern = /^[0-9\s\+]*$/;

    // If phone number exists and is invalid, restore from backend or clear
    if (
      currentPhoneNumber &&
      typeof currentPhoneNumber === "string" &&
      currentPhoneNumber.trim().length > 0
    ) {
      const isValid = phonePattern.test(currentPhoneNumber);

      if (!isValid) {
        // Try to restore from backend customerData
        let restoredPhoneNumber = "";
        try {
          const customerDataStr = localStorage.getItem("customerData");
          if (customerDataStr) {
            const customerDataObj = JSON.parse(customerDataStr) as Record<
              string,
              unknown
            >;
            const backendPhone = customerDataObj?.phoneNumber;
            if (
              backendPhone &&
              typeof backendPhone === "string" &&
              phonePattern.test(backendPhone)
            ) {
              restoredPhoneNumber = backendPhone;
            }
          }
        } catch {
          // ignore parse errors
        }

        // Update form data with restored value (or empty string if no valid backend value)
        setFormData(contactStepKey, {
          ...contactStepData,
          phoneNumber: restoredPhoneNumber,
        });

        console.log("📞 Invalid phone number detected and restored:", {
          invalid: currentPhoneNumber,
          restored: restoredPhoneNumber || "(cleared)",
        });
      }
    }
  }, [allFormData, setFormData]);

  // Validate and restore phone number when navigating away from contact details step
  const previousStepRef = useRef<number>(currentStep);
  useEffect(() => {
    const previousStep = previousStepRef.current;

    // If we just left step 5 (contact details), validate phone number
    if (previousStep === 5 && currentStep !== 5) {
      validateAndRestorePhoneNumber();
    }

    previousStepRef.current = currentStep;
  }, [currentStep, validateAndRestorePhoneNumber]);

  // If product was saved but missing locally after back navigation, restore from backend prefill
  useEffect(() => {
    if (currentStep !== 1) {
      productRestoreAttemptedRef.current = false;
      return;
    }
    if (productRestoreAttemptedRef.current) return;
    if (isNotReferredByCompany) return;

    const step1Data = (referedFromCompanyStepData ?? {}) as Record<
      string,
      unknown
    >;
    const hasCompany =
      typeof step1Data.referedFromCompany === "string" &&
      (step1Data.referedFromCompany as string).trim().length > 0;
    const hasProduct =
      typeof step1Data.productName === "string" &&
      (step1Data.productName as string).trim().length > 0;
    if (!hasCompany || hasProduct) {
      productRestoreAttemptedRef.current = true;
      return;
    }

    const userId = user?.userId;
    if (typeof userId !== "number" || userId <= 0) {
      productRestoreAttemptedRef.current = true;
      return;
    }

    (async () => {
      try {
        const prefillResponse = await OnboardingService.loadCustomerPrefillData(
          userId,
          { skipErrorCapture: true }
        );
        const serverStep1 = prefillResponse?.data?.["onboarding-step-1"] as
          | Record<string, unknown>
          | undefined;
        const serverCompany =
          typeof serverStep1?.referedFromCompany === "string"
            ? (serverStep1.referedFromCompany as string).trim()
            : "";
        const serverProduct =
          typeof serverStep1?.productName === "string"
            ? (serverStep1.productName as string).trim()
            : "";
        if (
          serverStep1 &&
          serverCompany.length > 0 &&
          serverCompany === (step1Data.referedFromCompany as string)
        ) {
          const nextData: Record<string, unknown> = {
            ...step1Data,
            productName: serverProduct || step1Data.productName || "",
            // Keep other fields as-is; server reference/accountType may also be applied if present
            ...(serverStep1.accountType
              ? { accountType: serverStep1.accountType }
              : {}),
            ...(serverStep1.referenceId
              ? { referenceId: serverStep1.referenceId }
              : {}),
          };
          setFormData("onboarding-step-1", nextData);
          // Reflect selection in live state to ensure UI select shows it immediately
          if (serverProduct) setProductSelectionLive(serverProduct);
        }
      } finally {
        productRestoreAttemptedRef.current = true;
      }
    })();
  }, [
    currentStep,
    isNotReferredByCompany,
    referedFromCompanyStepData,
    setFormData,
    user?.userId,
  ]);

  const clearCompanyReferralStep = async (dependencies: {
    currentForm: Record<string, unknown>;
    customerIdHint: number | null;
    userId: number | null;
    userEmail: string | undefined;
    setFormData: ReturnType<typeof useForm>["setFormData"];
    setSchemaOverrides: React.Dispatch<
      React.SetStateAction<Record<number, ScreenSchema>>
    >;
    setCompanySelectionLive: React.Dispatch<
      React.SetStateAction<string | undefined>
    >;
    setProductSelectionLive: React.Dispatch<
      React.SetStateAction<string | undefined>
    >;
    setNotReferredByCompanyLive: React.Dispatch<
      React.SetStateAction<boolean | undefined>
    >;
    setAccountTypeOptions: React.Dispatch<
      React.SetStateAction<AccountTypeRecord[]>
    >;
    setAccountTypeCompanyKey: React.Dispatch<
      React.SetStateAction<string | null>
    >;
    accountTypeRef: React.MutableRefObject<string | null>;
    ensureFieldStageCustomerId: () => Promise<number | null>;
    getCustomerSnapshot: () => Record<string, unknown> | null;
  }): Promise<void> => {
    const {
      currentForm,
      customerIdHint,
      userId,
      userEmail,
      setFormData: setFormDataFn,
      setSchemaOverrides: setSchemaOverridesFn,
      setCompanySelectionLive: setCompanySelectionLiveFn,
      setProductSelectionLive: setProductSelectionLiveFn,
      setNotReferredByCompanyLive: setNotReferredByCompanyLiveFn,
      setAccountTypeOptions: setAccountTypeOptionsFn,
      setAccountTypeCompanyKey: setAccountTypeCompanyKeyFn,
      accountTypeRef,
      ensureFieldStageCustomerId: ensureFieldStageCustomerIdFn,
      getCustomerSnapshot,
    } = dependencies;

    const clearedPayload: Record<string, unknown> = {};
    setFormDataFn("onboarding-step-1", clearedPayload);
    setSchemaOverridesFn((prev) => {
      if (!prev[1]) {
        return prev;
      }
      const next = { ...prev };
      delete next[1];
      return next;
    });
    setCompanySelectionLiveFn(undefined);
    setProductSelectionLiveFn(undefined);
    setNotReferredByCompanyLiveFn(undefined);
    setAccountTypeOptionsFn([]);
    setAccountTypeCompanyKeyFn(null);
    accountTypeRef.current = null;

    const effectiveUserId =
      typeof userId === "number" && userId > 0 ? userId : null;
    if (!effectiveUserId) {
      return;
    }

    const resolvedCustomerId =
      typeof customerIdHint === "number" && customerIdHint > 0
        ? customerIdHint
        : await ensureFieldStageCustomerIdFn();
    const customerId =
      typeof resolvedCustomerId === "number" && resolvedCustomerId > 0
        ? resolvedCustomerId
        : null;
    if (!customerId) {
      return;
    }

    try {
      const debts = await OnboardingService.getDebts(customerId, {
        skipErrorCapture: true,
      });
      if (Array.isArray(debts) && debts.length > 0) {
        await Promise.all(
          debts.map((debt) => {
            const debtId = typeof debt?.id === "number" ? debt.id : undefined;
            if (!debtId) {
              return undefined;
            }
            return OnboardingService.deleteDebts(customerId, debtId, {
              skipErrorCapture: true,
            });
          })
        );
      }
    } catch {
      // Debt cleanup failure is non-blocking but recorded if FEMA logs are enabled elsewhere
    }

    const customerSnapshot = getCustomerSnapshot();
    const recordCandidate = customerSnapshot?.id;
    const customerCandidate = customerSnapshot?.customerId;
    const resolvedRecordId =
      typeof recordCandidate === "number" ? recordCandidate : customerId;
    const resolvedCustomerIdForUpdate =
      typeof customerCandidate === "number" ? customerCandidate : customerId;

    try {
      await OnboardingService.updateCustomer(
        {
          ...currentForm,
          "onboarding-step-1": clearedPayload,
        },
        effectiveUserId,
        resolvedRecordId ?? customerId,
        resolvedCustomerIdForUpdate ?? customerId,
        typeof userEmail === "string" ? userEmail : "",
        { skipErrorCapture: true }
      );
    } catch {
      // Backend clearing failure is non-blocking
    }
  };

  // Show completion screen if onboarding is complete
  if (isOnboardingComplete) {
    return (
      <OnboardingCompletion
        userName={getUserName()}
        onContinue={handleCompletionContinue}
      />
    );
  }

  // Show loading screen while prefill data is being loaded
  if (!prefillLoaded && user?.userId) {
    return <SimpleLoadingScreen />;
  }

  // Show loading screen while on step 1 and lenders haven't loaded yet
  // This prevents incorrect field visibility due to missing API data
  if (currentStep === 1 && !lendersLoaded) {
    return <SimpleLoadingScreen />;
  }

  return (
    <>
      <ErrorDialog
        open={errorDialogOpen}
        title={errorDialogTitle}
        message={errorDialogMessage}
        onClose={() => setErrorDialogOpen(false)}
        primaryActionText="Close"
      />
      {/* Mobile - Show only slideshow with header */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          height: "100dvh",
          width: "100vw",
          backgroundColor: "#2C5F5D",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Fixed Mobile Header with Logo, Progress, Help, and Menu */}
        <Box
          ref={headerRef}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {/* Top row with Logo and Menu */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
              height: "56px",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="img"
                src="/icons/logo.png"
                alt="IE Hub"
                sx={{
                  width: 86,
                  height: 44,
                  borderRadius: 1,
                }}
              />
            </Box>

            {/* Hamburger Menu Button */}
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                color: "#033F44",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Mobile Menu */}
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={handleLogout}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "error.light",
                    color: "error.contrastText",
                  },
                }}
              >
                <LogoutIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>

          {/* Progress Bar and Help Row */}
          <Box
            sx={{
              px: 2,
              pb: 2,
              pt: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Progress Bar - Left side with limited width */}
            <Box sx={{ width: "30%" }}>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${progress}%`,
                    backgroundColor: "#155F54",
                    borderRadius: 3,
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
            </Box>

            {/* Help Button - Right side */}
            <Box
              onClick={handleHelpOpen}
              sx={{
                color: "text.secondary",
                textDecoration: "underline",
                fontSize: "0.875rem",
                cursor: "pointer",
                "&:hover": { color: "primary.main" },
              }}
            >
              Help?
            </Box>
          </Box>
        </Box>

        {/* Form Content with top padding to account for fixed header */}
        <Box
          ref={mobileContentRef}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            backgroundColor: "#fff",
            px: 2,
            pt: `calc(${headerHeight}px + 8px)`,
            pb: `calc(${footerHeight}px + env(safe-area-inset-bottom))`,
            overflow: "auto",
          }}
        >
          {/* Screen Component for Current Step */}
          <Box sx={{ flex: 1 }}>
            <OnboardingErrorBoundary
              onGoBack={handleBack}
              stepLabel={`step-${currentStep}`}
            >
              <Screen
                key={screenRenderKey}
                schema={currentSchema}
                hideHeader={hideHeader}
                hideActions={hideActions}
                initialData={allFormData[`onboarding-step-${currentStep}`] || {}}
                onSubmit={handleFormSubmit}
                onValuesChange={handleValuesChange}
                formId={`onboarding-form-mobile-${currentStep}`}
              />
            </OnboardingErrorBoundary>
          </Box>
        </Box>

        {/* Bottom Navigation - Mobile (Fixed) */}
        <Box
          ref={footerRef}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            backgroundColor: "#fff",
          }}
        >
          <Footer
            handleBack={handleBack}
            shouldApplyTopBorder={true}
            shouldBackDisabled={currentStep === earliestStep}
            nextButtonText={computedPrimaryText}
            formId={`onboarding-form-mobile-${currentStep}`}
            nextButtonLoading={Boolean(loading?.["onboarding-submit"])}
            showSecondaryButton={shouldShowSecondary}
            secondaryButtonText={secondaryButtonText}
            onSecondaryClick={handleSkip}
            secondaryButtonLoading={Boolean(loading?.["onboarding-skip"])}
          />
        </Box>
      </Box>

      {/* Desktop - Left side form with grey background, Right side carousel */}
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: { xs: "none", md: "flex" },
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Left Side - Form with Grey Background */}
        <Box
          sx={{
            flex: "1 1 auto",
            backgroundColor: "#f5f5f5", // Grey background for left side
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          {/* White Form Container */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "500px",
              height: "100%",
              backgroundColor: "#ffffff", // White background for form
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Fixed Header with Logo, Progress, Help, and Menu */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 100,
                backgroundColor: "#ffffff",
                borderBottom: "1px solid #e0e0e0",
                borderRadius: "12px 12px 0 0", // Match container border radius
              }}
            >
              {/* Top row with Logo, Help, and Menu */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  minHeight: "64px",
                }}
              >
                {/* Logo */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component="img"
                    src="/icons/logo.png"
                    alt="IE Hub"
                    sx={{
                      width: 86,
                      height: 44,
                      borderRadius: 1,
                    }}
                  />
                </Box>

                {/* Hamburger Menu */}
                <IconButton
                  onClick={handleLogoutMenuOpen}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>

                {/* Logout Menu */}
                <Menu
                  anchorEl={logoutMenuAnchor}
                  open={Boolean(logoutMenuAnchor)}
                  onClose={handleLogoutMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      color: "error.main",
                      "&:hover": {
                        backgroundColor: "error.light",
                        color: "error.contrastText",
                      },
                    }}
                  >
                    <LogoutIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>

              {/* Progress Bar and Help Row */}
              <Box
                sx={{
                  px: 3,
                  pb: 2,
                  pt: 0,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Progress Bar - Left side with limited width */}
                <Box sx={{ width: "30%" }}>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${progress}%`,
                        backgroundColor: "#155F54",
                        borderRadius: 3,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>

                {/* Help Button - Right side */}
                <Box
                  onClick={handleHelpOpen}
                  sx={{
                    color: "text.secondary",
                    textDecoration: "underline",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  Help?
                </Box>
              </Box>
            </Box>

            {/* Form Content Area with padding for sticky header */}
            <Box
              ref={desktopContentRef}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
              }}
            >
              {/* Screen Component */}
              <Box sx={{ flex: 1, px: 3, py: 1.5 }}>
                <OnboardingErrorBoundary
                  onGoBack={handleBack}
                  stepLabel={`step-${currentStep}`}
                >
                  <Screen
                    key={screenRenderKey}
                    schema={currentSchema}
                    hideHeader={hideHeader}
                    hideActions={hideActions}
                    initialData={
                      allFormData[`onboarding-step-${currentStep}`] || {}
                    }
                    onSubmit={handleFormSubmit}
                    onValuesChange={handleValuesChange}
                    formId={`onboarding-form-desktop-${currentStep}`}
                  />
                </OnboardingErrorBoundary>
              </Box>
            </Box>

            {/* Bottom Section with Back Button and Continue Button - Desktop Only */}
            <Box
              sx={{
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
              }}
            >
              <Footer
                handleBack={handleBack}
                shouldApplyTopBorder={false}
                shouldApplyBackgroundColor={false}
                shouldBackDisabled={currentStep === earliestStep}
                nextButtonText={computedPrimaryText}
                formId={`onboarding-form-desktop-${currentStep}`}
                nextButtonLoading={Boolean(loading?.["onboarding-submit"])}
                showSecondaryButton={shouldShowSecondary}
                secondaryButtonText={secondaryButtonText}
                onSecondaryClick={handleSkip}
                secondaryButtonLoading={Boolean(loading?.["onboarding-skip"])}
              />
            </Box>
          </Box>
        </Box>

        {/* Right Side - Carousel - Desktop Only */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: "max(5vw, calc(100vh * 9 / 16))",
            display: { xs: "none", md: "flex" },
            alignItems: "flex-start",
            justifyContent: "flex-end",
            position: "relative",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          {/* 9:16 Image Wrapper - flush to right */}
          <Box
            sx={{
              width: "100%",
              aspectRatio: "9 / 16",
            }}
          >
            <Box
              component="img"
              src={carouselImages[currentImageIndex]}
              alt="Carousel"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "left center",
                display: "block",
                transition: "opacity 0.5s ease-in-out",
              }}
            />
          </Box>

          {/* Pagination dots */}
          <Box
            sx={{
              position: "absolute",
              bottom: 30,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              justifyContent: "center",
              gap: 1.5,
              zIndex: 2,
            }}
          >
            {carouselImages.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor:
                    index === currentImageIndex
                      ? "rgba(255, 255, 255, 0.9)"
                      : "rgba(255, 255, 255, 0.4)",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.9)",
                  },
                }}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Help dialog is mounted globally via HelpProvider */}
    </>
  );
}
